import { useEffect, useRef, useMemo, useState } from "react";
import type { StateMap } from "../types";
import { showAlert, showConfirm, showPrompt } from "../lib/dialog";
import { cardOf, type HiddenMap } from "../lib/hiddenFixes";
import { loadAnthropicKey, loadAhrefsKey, loadDataForSeoCredentials } from "./SettingsModal";
import { ParentPickerDialog } from "./ParentPickerDialog";
import type { WPClient } from "../lib/wpClient";
import { describeExistingSchema, willSchemaFixBeManual } from "../lib/fixExecutor";

// Tabs that use the refined visual system. App.tsx adds the `sec-refined` class from this set (which
// drives the shared CSS in app.css); AuditSection uses it for the icon-free "Approve" label and the
// `ac-vstrip` marker that turns on the Option-3 vertical control strip (tinted fills + monoline SVG
// icons — see .ac-vstrip in app.css). Add a tab here to refine it; remove it to revert.
export const REFINED_TABS = new Set(["overview", "competitors", "anchors", "refdomains", "onpage", "speed", "schema", "toppages", "roadmap", "cannibal", "gbp", "llm", "gaps", "faqs", "govlinks", "citations", "urls", "internal", "entities", "contentdrawer", "expressschema", "expressinternal", "expressmeta", "lowhangingkw", "redirectionmanager", "llmtracking", "organicrank", "llmvisibility", "servicearea", "conversational"]);
// Tabs whose buttons are NOT blanket-refined: they either keep their original in-place controls
// (Top Pages / FAQs / Pages & Meta, Content Drawer, Service Area create cards) or need per-card
// handling (Internal Links — see acRefined).
const KEEP_BUTTONS = new Set(["toppages", "faqs", "urls", "internal", "contentdrawer", "servicearea"]);
const REFINED_CONTROLS = (id: string) => REFINED_TABS.has(id) && !KEEP_BUTTONS.has(id);
// Internal Links: only the Fix Cards (.ac inside an .oc card) get the refined/icon-free treatment;
// the Key Linking Opportunities table (.ac inside a .tp-act cell, not in an .oc) keeps its buttons.
const acIsRefined = (id: string, ac: HTMLElement) => REFINED_CONTROLS(id) || (id === "internal" && !!ac.closest(".oc"));
// Tabs whose fix cards never write to WordPress — the "⚡ Approve" button is stripped so only the
// manual Ignore/Complete tracking controls remain. Citations (directory listings), GBP (Google
// Business Profile optimization), and Gov/Local Links (link outreach) are all off-site manual work.
// Overview is a summary/roadmap — the actual approve actions live in the dedicated per-topic tabs.
// Competitors is analysis-only (benchmarking against rival sites) — nothing writes to WordPress.
// Roadmap is a strategic plan; Cannibalization is analysis + consolidation guidance — both manual.
// Page Speed recommendations (image optimization, caching, render-blocking assets, etc.) aren't
// one-click WordPress writes — they're manual/hosting work, so only the tracking controls remain.
const NO_APPROVE_TABS = new Set(["citations", "gbp", "govlinks", "overview", "competitors", "roadmap", "cannibal", "speed"]);

// ── Create-form draft persistence ─────────────────────────────────────────────
// The generated article content lives only in the DOM (the contenteditable editor) and the
// edited form fields live in the section's HTML. Switching tabs re-renders the section from
// its stored HTML string, which wipes both. We persist them per card (namespaced by the
// active site) so a tab switch / reload doesn't lose generated content or field edits.
const CREATE_DRAFTS_KEY = "lsg_create_drafts";
const CFT_FIELD_KEYS = ["title", "mt", "sl", "par", "md", "pt", "nil", "pd", "wc", "kw", "ci", "type", "summaryheading", "summarize"];
interface CreateDraft { fields?: Record<string, string>; content?: string; faqAnswer?: string }
const activeSiteKey = (): string => { try { return localStorage.getItem("lsg_active_site") ?? ""; } catch { return ""; } };
const draftKey = (fixId: string): string => `${activeSiteKey()}::${fixId}`;
function loadCreateDrafts(): Record<string, CreateDraft> {
  try { return JSON.parse(localStorage.getItem(CREATE_DRAFTS_KEY) ?? "{}") as Record<string, CreateDraft>; } catch { return {}; }
}
function getCreateDraft(fixId: string): CreateDraft | undefined { return loadCreateDrafts()[draftKey(fixId)]; }
function patchCreateDraft(fixId: string, patch: CreateDraft): void {
  try { const all = loadCreateDrafts(); const k = draftKey(fixId); all[k] = { ...all[k], ...patch }; localStorage.setItem(CREATE_DRAFTS_KEY, JSON.stringify(all)); } catch { /* ignore */ }
}
export function removeCreateDraft(fixId: string): void {
  try { const all = loadCreateDrafts(); const k = draftKey(fixId); if (all[k]) { delete all[k]; localStorage.setItem(CREATE_DRAFTS_KEY, JSON.stringify(all)); } } catch { /* ignore */ }
}

// ── WYSIWYG heading-level indicator ──────────────────────────────────────────
// A single floating "H2/H3…" badge shown while the caret/selection sits inside a heading in a
// rich-text editor, plus one document-level selection tracker (replaced whenever a section re-wires)
// that also keeps the toolbar's Heading dropdown in sync with the caret's current block level.
let rtHeadingBadge: HTMLElement | null = null;
function rtGetHeadingBadge(): HTMLElement {
  if (!rtHeadingBadge || !document.body.contains(rtHeadingBadge)) {
    rtHeadingBadge = document.createElement("div");
    rtHeadingBadge.className = "rt-heading-badge";
    rtHeadingBadge.style.display = "none";
    document.body.appendChild(rtHeadingBadge);
  }
  return rtHeadingBadge;
}
let rtSelectionTracker: (() => void) | null = null;
function captureCreateFields(fixId: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of CFT_FIELD_KEYS) {
    const elx = document.getElementById(`cft-${k}-${fixId}`) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
    if (!elx) continue;
    // The summarization toggle is a checkbox — persist its checked state, not its value.
    out[k] = (elx instanceof HTMLInputElement && elx.type === "checkbox") ? (elx.checked ? "1" : "") : elx.value;
  }
  return out;
}
const draftTimers = new Map<string, ReturnType<typeof setTimeout>>();
function scheduleDraftSave(fixId: string, fn: () => void): void {
  const t = draftTimers.get(fixId); if (t) clearTimeout(t);
  draftTimers.set(fixId, setTimeout(fn, 400));
}

// Month-Day-Year pattern used in audit section headers and notes.
// Matches "May 13 2026" (no comma, used in .atag spans) and
// "May 13, 2026" (with comma, used in prose text).
const DATE_RE = /\b(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}\b/g;

function applyAuditDate(html: string, date: string): string {
  if (!date) return html;
  // atag spans use "Month D YYYY" (no comma); prose uses "Month D, YYYY"
  const noComma = date.replace(",", "");
  return html.replace(DATE_RE, (match) => match.includes(",") ? date : noComma);
}

interface Props {
  id: string;
  html: string;
  state: StateMap;
  siteBrand?: string;
  auditDate?: string;
  onApprove: (id: string, message?: string, isCreate?: boolean) => void;
  onIgnore:  (id: string, checked: boolean) => void;
  onDone:    (id: string, checked: boolean) => void;
  onReset:   (id: string) => void;
  hidden:    HiddenMap;
  onToggleHide: (id: string) => void;
  connected: boolean;
  fetchPageTags: (pathSlug: string) => Promise<string[] | null>;
  onOpenSchemaSetup?: () => void;
  onViewSchema?: (json: string, url: string) => void; // show a schema card's JSON-LD in a copy popup
  onViewIssues?: (issues: string[], url: string) => void; // show the auto-fixed schema.org issues in a popup
  manualSchema?: Set<string>;                         // schema fix-ids that are manual (merge-popup) cards
  onManualUpdate?: (id: string) => void;              // clicking a "Manual Update" card re-opens the popup
  onPredictManual?: (id: string) => void;             // a schema card is predicted manual up front (label it before any click)
  getClient?: () => WPClient | null; // powers the New Page Details "Search Parent" / "Search Post Category" picker
  // Force the original horizontal button strip even when this section's `id` would normally get the
  // refined vertical control strip. Used by the Express tools (Bulk Page Schema / Internal Links /
  // Meta Data), which render cards with id="schema"/"internal"/"urls" inside a refined wrapper but
  // must keep their compact horizontal controls unchanged.
  keepButtons?: boolean;
}

// Last URL path segment → WordPress page slug (mirrors the executor's resolution).
function slugFromHref(href: string): string {
  try {
    const u = new URL(/^https?:\/\//.test(href) ? href : `https://x.example${href.startsWith("/") ? "" : "/"}${href}`);
    return u.pathname.replace(/\/+$/, "").split("/").filter(Boolean).pop() ?? "";
  } catch {
    return href.replace(/[#?].*$/, "").replace(/\/+$/, "").split("/").filter(Boolean).pop() ?? "";
  }
}

/**
 * AuditSection
 *
 * Renders a report section's raw HTML inside a <section> tag,
 * then imperatively wires all action controls (approve buttons,
 * ignore checkboxes, done checkboxes) to the React state engine.
 *
 * This approach preserves the exact HTML/CSS of the original report
 * while giving React full control of state.
 */
// Expose countChars globally so oninput="countChars(...)" attributes work in .um-edit textareas.
if (typeof window !== "undefined" && !(window as unknown as Record<string, unknown>).countChars) {
  (window as unknown as Record<string, unknown>).countChars = (
    inputId: string,
    counterId: string,
    limit: number
  ) => {
    const input = document.getElementById(inputId) as HTMLInputElement | HTMLTextAreaElement | null;
    const counter = document.getElementById(counterId);
    if (!input || !counter) return;
    const len = input.value.length;
    counter.textContent = `${len} / ${limit} chars`;
    counter.className = len > limit ? "um-char over" : "um-char";
  };
}

// Strips "Audit date: May 29, 2026." (any date, any capitalisation, optional period)
const AUDIT_DATE_LABEL_RE = /Audit\s+[Dd]ate:\s*(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}\.?\s*/g;

export function AuditSection({ id, html, state, siteBrand, auditDate, onApprove, onIgnore, onDone, onReset, hidden, onToggleHide, connected, fetchPageTags, onOpenSchemaSetup, onViewSchema, onViewIssues, manualSchema, onManualUpdate, onPredictManual, getClient, keepButtons }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  // Whether a given .ac gets the refined vertical strip. `keepButtons` (Express tools) forces the
  // original horizontal controls even for ids that would otherwise refine (schema/internal).
  const acRefined = (ac: HTMLElement) => !keepButtons && acIsRefined(id, ac);
  // New Page Details parent/category picker: which create-form field is being edited + which mode.
  const [parentPicker, setParentPicker] = useState<{ fixId: string; mode: "page" | "post" } | null>(null);
  // The action controls are wired imperatively in a [html]-deps effect, so their
  // listeners would capture stale callbacks. Route every call through this ref
  // (refreshed on each render) so handlers always invoke the LATEST props — e.g.
  // handleApprove reflects a WP connection made after the section rendered.
  const cbRef = useRef({ onApprove, onIgnore, onDone, onReset, onToggleHide, onOpenSchemaSetup, onViewSchema, onViewIssues, manualSchema, onManualUpdate, onPredictManual });
  cbRef.current = { onApprove, onIgnore, onDone, onReset, onToggleHide, onOpenSchemaSetup, onViewSchema, onViewIssues, manualSchema, onManualUpdate, onPredictManual };
  const processedHtml = useMemo(() => {
    let out = applyAuditDate(html, auditDate ?? "");
    if (id === "competitors") out = out.replace(AUDIT_DATE_LABEL_RE, "");
    // Remove "Ahrefs API · Live · [date]" atag from the overview header — date is shown in the toolbar
    if (id === "overview") out = out.replace(
      /<span\s+class="atag">[^<]*Ahrefs[^<]*(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}[^<]*<\/span>/gi,
      ""
    );
    return out;
  }, [id, html, auditDate]);

  // Wire all action controls whenever HTML or state changes
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // ── Pages & Meta: strip retired section dividers from previously-generated reports ──
    // The generator no longer emits either divider. For the "New Pages — Recommended for
    // Creation" divider, also remove every following sibling in the .sub (the new-page create
    // cards are always the last content there). The plain "Existing Pages — Meta Updates
    // Recommended" divider is removed on its own (the meta-update cards after it stay).
    if (id === "urls") {
      const newDivider = el.querySelector<HTMLElement>(".um-section-divider.um-section-new");
      if (newDivider) {
        let node: Element | null = newDivider;
        while (node) { const next: Element | null = node.nextElementSibling; node.remove(); node = next; }
      }
      el.querySelectorAll<HTMLElement>(".um-section-divider").forEach(d => d.remove());
    }

    // ── Content slug/URL links must open the REAL page in a new tab, not the app ──
    // Regenerated content sometimes renders site paths as relative links (href="/slug"),
    // which the browser resolves against localhost (the running app). Rewrite same-site
    // relative anchors to absolute URLs on the audited domain and force a new tab.
    {
      let origin = "";
      try {
        const fm = JSON.parse(localStorage.getItem("seo_full_metrics") ?? "null") as { sites?: Array<{ domain?: string }> } | null;
        const dom = fm?.sites?.[0]?.domain
          ?? (document.getElementById("cover-website-url") as HTMLInputElement | null)?.value?.trim();
        const clean = dom ? dom.replace(/^https?:\/\//i, "").replace(/\/.*$/, "") : "";
        if (clean) origin = `https://${clean}`;
      } catch { /* ignore — leave relative links untouched if no domain known */ }
      el.querySelectorAll<HTMLAnchorElement>('a[href^="/"]').forEach(a => {
        const href = a.getAttribute("href") ?? "";
        if (href.startsWith("//")) return; // protocol-relative (external) — leave as-is
        if (origin) a.setAttribute("href", origin + href);
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
      });
    }

    // ── Make the "Rec. H1" field editable (Pages & Meta cards) ───────────────
    // Static cards render Rec. H1 as a read-only <div class="h1-rec">, so it
    // can't be edited and the executor (which reads et-{slug}-h1) sees nothing.
    // Convert it into an editable textarea matching the Rec. Title/Desc fields.
    el.querySelectorAll<HTMLElement>(".h1-rec").forEach(div => {
      const card = div.closest(".um");
      if (!card) return;
      // Derive the slug from a sibling editable field id (et-{slug}-title) or cur-{slug}-h1.
      let slug = "";
      const titleEl = card.querySelector('[id^="et-"][id$="-title"]');
      const tm = titleEl?.id.match(/^et-(.+)-title$/);
      if (tm) slug = tm[1];
      if (!slug) {
        const curEl = card.querySelector('[id^="cur-"][id$="-h1"]');
        const cm = curEl?.id.match(/^cur-(.+)-h1$/);
        if (cm) slug = cm[1];
      }
      if (!slug) return;
      if (card.querySelector(`[id="et-${slug}-h1"]`)) return; // already editable
      const value = (div.textContent ?? "").trim();
      const ta = document.createElement("textarea");
      ta.className = "um-edit";
      ta.id = `et-${slug}-h1`;
      ta.value = value;
      ta.setAttribute("oninput", `countChars('et-${slug}-h1','ec-${slug}-h1',70)`);
      const counter = document.createElement("div");
      counter.className = "um-char";
      counter.id = `ec-${slug}-h1`;
      div.replaceWith(ta, counter);
      (window as unknown as Record<string, ((a: string, b: string, c: number) => void)>)
        .countChars?.(`et-${slug}-h1`, `ec-${slug}-h1`, 70);
    });

    // ── "Current" crawl fields are read-only ─────────────────────────────────
    // Current H1 / Meta Title / Meta Desc show the existing on-page values, not
    // something to edit. Mark them read-only (programmatic auto-fill still works).
    el.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(".um-crawl-input").forEach(f => {
      f.readOnly = true;
      f.style.cursor = "default";
      if (/paste|auto-fill/i.test(f.placeholder)) f.placeholder = "Current on-page value (read-only)";
    });

    // ── New-page (Create) Tags field: placeholder + a "Post or Page" selector ──
    el.querySelectorAll<HTMLInputElement>('input[id^="cft-pt-"]').forEach(f => {
      f.placeholder = "Add tags to new page (if applicable)";
      const fixId = f.id.replace(/^cft-pt-/, "");
      // Insert after the 3-field tag row (Meta Desc | Page Tags | Article Filter Tags) when
      // present, so the injected Post/Page + Word Count fields don't split that row.
      const wrap = f.closest(".cfp-tagrow") ?? f.closest(".cfp-field");
      if (wrap && !el.querySelector(`[id="cft-type-${fixId}"]`)) {
        // Default to Page for all new pages (the user can switch to Post when needed).
        const field = document.createElement("div");
        field.className = "cfp-field";
        field.innerHTML =
          `<label class="cfp-label">Post or Page</label>` +
          `<select id="cft-type-${fixId}" class="cfp-input">` +
            `<option value="page" selected>Page</option>` +
            `<option value="post">Post</option>` +
          `</select>`;
        wrap.insertAdjacentElement("afterend", field); // directly under the Tags field

        // Word Count target — directly under Post or Page. Optional: blank = auto
        // (crawl the top 5 ranking pages, average their word count, generate +15%).
        const wc = document.createElement("div");
        wc.className = "cfp-field";
        wc.innerHTML =
          `<label class="cfp-label">Word Count <span style="font-weight:400;text-transform:none;color:#94a3b8">(optional)</span>` +
            `<span class="audit-tip" style="margin-left:4px"><span class="audit-tip-icon">?</span>` +
            `<span class="audit-tip-box"><strong>Word Count (optional)</strong>` +
            `<p>Leave this blank and the Generate Content button will crawl the top 5 pages ranking for the Primary Keyword, work out their average word count, and generate content about <strong>15% longer</strong> to beat the competition.</p>` +
            `<p>To manually override that target, enter a number in this field.</p></span></span>` +
          `</label>` +
          `<input id="cft-wc-${fixId}" type="number" min="300" step="50" class="cfp-input" placeholder="Auto — top 5 average + 15%" />`;
        field.insertAdjacentElement("afterend", wc);
      }

      // Normalize EVERY create form to the current standard so the New Page Details form is
      // identical across every tab — runs regardless of whether the Post/Page field above
      // was just injected. Older generated forms (e.g. the PAA FAQs tab) prefilled the tag
      // fields and lacked the Custom Instructions box. Once-per-load guard via the panel.
      const panel = f.closest<HTMLElement>(".create-form-panel");
      if (panel && !panel.getAttribute("data-np-normalized")) {
        panel.setAttribute("data-np-normalized", "true");
        // Legacy baked forms prefilled tag fields that shouldn't carry over → blank them.
        // Current-standard forms (data-cfp-std, built by cfrPanel) set their tags on purpose
        // (e.g. the user's own Service Area Page Tags), so keep those.
        if (!panel.hasAttribute("data-cfp-std")) {
          f.value = ""; // blank prefilled Page Tags
        } else {
          // "InnerPage" must NEVER be a default in the Page Tags field of any new-page dialog.
          // Strip it from the prefilled default (the user can still add it manually per card);
          // page-vs-post is governed by the Post/Page selector, so InnerPage isn't needed here.
          const parts = f.value.split(",").map(t => t.trim()).filter(Boolean);
          const cleaned = parts.filter(t => t.toLowerCase() !== "innerpage");
          if (cleaned.length !== parts.length) f.value = cleaned.join(", ");
        }
        // Legacy forms carried an "Article Filter Tags" field (cft-aft); the current standard
        // replaces it with "Number of Internal Links" (cft-nil). Swap it in-place so older forms
        // match the new standard. New forms already ship cft-nil, so this is a no-op there.
        const aftField = el.querySelector<HTMLInputElement>(`[id="cft-aft-${fixId}"]`);
        if (aftField && !el.querySelector(`[id="cft-nil-${fixId}"]`)) {
          const wrap = aftField.closest<HTMLElement>(".cfp-field");
          if (wrap) {
            wrap.innerHTML =
              `<label class="cfp-label">Number of Internal Links` +
                `<span class="audit-tip" style="margin-left:4px"><span class="audit-tip-icon">?</span>` +
                `<span class="audit-tip-box"><strong>Number of Internal Links</strong>` +
                `<p>This number determines the number of internal links auto-added into the content generation up to 5. It is limited to ensure sufficient and relevant internal links can be added in the existing content.</p>` +
                `<p>You can still add instructions in the Custom Instructions textfield to link to a specific page independent of this setting.</p></span></span>` +
              `</label>` +
              `<input id="cft-nil-${fixId}" type="number" min="0" max="5" step="1" class="cfp-input" value="3"/>`;
          }
        }
        // Enable Article Summarization toggle + Summary Heading field, injected for older
        // forms that predate it (new forms carry it from cfrPanel). Goes ABOVE Custom
        // Instructions: appended to the grid here, before the ci field is appended below.
        if (!el.querySelector(`[id="cft-summarize-${fixId}"]`)) {
          const grid = f.closest(".cfp-grid");
          if (grid) {
            const sm = document.createElement("div");
            sm.className = "cfp-field cfp-full cfp-field-plain cfp-summ-field";
            sm.innerHTML =
              `<label class="cfp-summ-toggle" style="display:flex;align-items:center;gap:9px;cursor:pointer;margin:0">` +
                `<input type="checkbox" id="cft-summarize-${fixId}" class="cft-summarize-cb" data-fix-id="${fixId}"/>` +
                `<span class="cfp-label" style="margin:0">Enable Article Summarization</span>` +
                `<span class="audit-tip" style="margin-left:2px"><span class="audit-tip-icon">?</span><span class="audit-tip-box"><strong>Article Summarization</strong><p>When enabled, a short summary is added at the very top of the article — a handful of bullet points covering the article's most important aspects.</p><p>The <strong>Summary Heading Selection</strong> text is shown above the bullets in normal font (not a heading tag).</p></span></span>` +
              `</label>` +
              `<div id="cft-summaryheading-wrap-${fixId}" class="cft-summ-head-wrap" style="display:none;margin-top:9px">` +
                `<label class="cfp-label">Summary Heading Selection <span style="font-weight:400;text-transform:none;color:#94a3b8">(shown above the bullets, normal font)</span></label>` +
                `<input id="cft-summaryheading-${fixId}" type="text" class="cfp-input" value="" placeholder="e.g. Key Takeaways"/>` +
              `</div>`;
            grid.appendChild(sm);
          }
        }
        if (!el.querySelector(`[id="cft-ci-${fixId}"]`)) {
          const grid = f.closest(".cfp-grid");
          if (grid) {
            const ci = document.createElement("div");
            ci.className = "cfp-ci-field";
            ci.innerHTML =
              `<label class="cfp-label">Custom Instructions <span style="font-weight:400;text-transform:none;color:#94a3b8">(optional)</span>` +
                `<span class="audit-tip" style="margin-left:4px"><span class="audit-tip-icon">?</span>` +
                `<span class="audit-tip-box"><strong>Custom Instructions</strong>` +
                `<p>Any instructions placed here will be considered when creating the new page content. Currently, a minimum of three internal links are already added to each new page so this instruction is not needed unless you want to specify URLs or need more than 3 internal links.</p>` +
                `<p>If this box is empty or normal instructions are not overridden, it will continue with normal instructions (minimum 3 internal links, service pages prioritized when linking, and a &quot;Contact Us&quot; CTA). Schema will also be added upon publishing to WordPress.</p></span></span>` +
              `</label>` +
              `<textarea id="cft-ci-${fixId}" class="cfp-input cfp-ta" placeholder="Optional — special instructions for this page (e.g. specific URLs to link, more than 3 internal links, tone, or sections to include)"></textarea>`;
            grid.appendChild(ci);
          }
        }
        // Wire the summarization toggle → reveal/hide the Summary Heading field. Guarded
        // once per panel by data-np-normalized, so listeners are never duplicated.
        const summCb = el.querySelector<HTMLInputElement>(`[id="cft-summarize-${fixId}"]`);
        const summWrap = el.querySelector<HTMLElement>(`[id="cft-summaryheading-wrap-${fixId}"]`);
        if (summCb && summWrap) {
          const syncSumm = () => { summWrap.style.display = summCb.checked ? "block" : "none"; };
          syncSumm();
          summCb.addEventListener("change", syncSumm);
        }
        // Older forms baked a "10%" Word Count tooltip/placeholder; the target is now +15%.
        // Update any stale 10% text so the displayed promise matches actual generation.
        const wcEl = el.querySelector<HTMLInputElement>(`[id="cft-wc-${fixId}"]`);
        if (wcEl) {
          if (wcEl.placeholder.includes("10%")) wcEl.placeholder = wcEl.placeholder.replace(/10%/g, "15%");
          const tip = wcEl.closest(".cfp-field")?.querySelector(".audit-tip-box");
          if (tip && tip.innerHTML.includes("10%")) tip.innerHTML = tip.innerHTML.replace(/10%/g, "15%");
        }
      }
    });

    // ── Manual-only tabs: no WordPress action ever runs from these cards, so strip
    // the "⚡ Approve" button before it's wired. Citations = directory/listing work,
    // GBP = Google Business Profile optimization, Gov/Local Links = outreach. The
    // Ignore/Complete controls stay for manual progress tracking. Runs on both
    // freshly-crawled HTML and older cached HTML, and drops any leading .ac-sep
    // divider the removed button left behind. (Never touches Create buttons — these
    // tabs have none.)
    if (NO_APPROVE_TABS.has(id)) {
      el.querySelectorAll<HTMLButtonElement>(".ac-btn:not(.create-expand-btn)").forEach(btn => {
        const next = btn.nextElementSibling;
        if (next?.classList.contains("ac-sep")) next.remove();
        btn.remove();
      });
    }

    // ── Overview: its fix-card sub-section was labeled "…with WordPress Action
    // Controls", which no longer fits now the Approve buttons are stripped (the
    // actual actions live in the per-topic tabs). Retitle it on cached HTML so the
    // label matches; new crawls already emit the shorter label. ──────────────────
    if (id === "overview") {
      el.querySelectorAll<HTMLElement>(".sub-lbl").forEach(lbl => {
        // Only when the label is plain text (no nested tooltip/markup to preserve).
        if (lbl.children.length === 0 && /priority fixes/i.test(lbl.textContent ?? "") && /wordpress action controls/i.test(lbl.textContent ?? "")) {
          lbl.textContent = "Priority Fixes";
        }
      });
    }

    // ── Wire approve/confirmApprove buttons ──────────────────
    el.querySelectorAll<HTMLButtonElement>(".ac-btn").forEach(btn => {
      const ac = btn.closest(".ac") as HTMLElement | null;
      if (!ac) return;
      const acId = ac.id?.replace(/^ac-/, "");
      if (!acId) return;

      const clone = btn.cloneNode(true) as HTMLButtonElement;
      btn.replaceWith(clone);

      // Mark buttons that should say "Create" instead of "Approve" in visual sync.
      // FAQs tab: all buttons are "Create" (adding FAQ to site).
      // URLs tab: new page cards identified by red .b.br badge or existing "Create" button text.
      if (id === "faqs") {
        clone.setAttribute("data-is-create", "true");
      } else if (id === "urls") {
        const isNewPage = !!ac.closest(".um")?.querySelector(".um-title .b.br")
          || (btn.textContent ?? "").toLowerCase().includes("create");
        if (isNewPage) clone.setAttribute("data-is-create", "true");
      }

      clone.addEventListener("click", () => {
        if (clone.disabled) return;
        // Read current class from DOM — visual sync keeps this live even though
        // the onApprove closure may be stale from the last html-change render.
        if (clone.classList.contains("done-btn")) return;
        // "Manual Update" schema card — re-open its merge popup instead of approving/queuing.
        if (cbRef.current.manualSchema?.has(acId)) { cbRef.current.onManualUpdate?.(acId); return; }
        const msg = clone.getAttribute("data-confirm-msg") ?? undefined;
        cbRef.current.onApprove(acId, msg, clone.getAttribute("data-is-create") === "true");
      });

      // Store confirm message from onclick attribute if present
      const originalOnclick = btn.getAttribute("onclick") ?? "";
      const msgMatch = originalOnclick.match(/confirmApprove\([^,]+,\s*'([^']+)'\)/);
      if (msgMatch) clone.setAttribute("data-confirm-msg", msgMatch[1]);
    });

    // ── Inject a "Hide" toggle on every fix card (incl. cards with no Approve
    // button, e.g. GBP). Hiding a fix that doesn't apply keeps it in the app
    // (dimmed, with a Show button) but removes it from all exports. ──────────
    el.querySelectorAll<HTMLElement>(".ac").forEach(ac => {
      const acId = ac.id?.replace(/^ac-/, "");
      if (!acId) return;

      // Hide toggle — inserted first so it sits to the RIGHT of the reset circle.
      if (!ac.querySelector(":scope > .ac-hide")) {
        const hide = document.createElement("button");
        hide.type = "button";
        hide.className = "ac-hide";
        // Plain "Hide" text in both strips — no emoji. Refined vertical strips add a CSS eye-off
        // icon via ::before; horizontal strips stay icon-free (per design).
        hide.textContent = "Hide";
        hide.style.cssText =
          "display:inline-flex;align-items:center;justify-content:center;height:22px;padding:0 9px;" +
          "border-radius:11px;border:1px solid var(--bdr,#d1d5db);background:#fff;color:var(--ink3,#6b7280);" +
          "font-family:Mulish,sans-serif;font-size:10.5px;font-weight:700;cursor:pointer;margin-right:8px;" +
          "vertical-align:middle;flex-shrink:0;white-space:nowrap;";
        hide.addEventListener("click", (e) => { e.preventDefault(); e.stopPropagation(); cbRef.current.onToggleHide(acId); });
        ac.insertBefore(hide, ac.firstChild);
      }

      // Reset circle — only on cards with an Approve/Create action. Placed to the
      // LEFT of the Hide button. Clears the fix's status (queued OR done) so a completed
      // card can be re-approved. Hidden by default; the visual-sync effect shows it while
      // queued or completed.
      // NOT on schema cards (any card carrying a .schema-payload — Schema tab, Express Schema,
      // and the schema cards on the Entities tab): re-approving a schema card is handled via its
      // own controls, so the reset circle is omitted there.
      const isSchemaCard = !!ac.closest(".oc")?.querySelector(".schema-payload");
      if (ac.querySelector(".ac-btn") && !isSchemaCard && !ac.querySelector(":scope > .ac-reset")) {
        const reset = document.createElement("button");
        reset.type = "button";
        reset.className = "ac-reset";
        // Styled tooltip (data-tip) instead of the browser's unstyled title="" popup; aria-label
        // keeps the accessible name since this button is icon-only.
        reset.setAttribute("data-tip", "Reset — clear this fix's status so you can edit the fields and resubmit to WordPress");
        reset.setAttribute("aria-label", "Reset — clear this fix's status so you can edit the fields and resubmit to WordPress");
        reset.textContent = "↻";
        reset.style.cssText =
          "display:none;align-items:center;justify-content:center;width:22px;height:22px;" +
          "border-radius:50%;border:1px solid #2563eb;background:#fff;color:#2563eb;" +
          "font-size:13px;line-height:1;cursor:pointer;padding:0;margin-right:8px;vertical-align:middle;flex-shrink:0;opacity:1;";
        reset.addEventListener("mouseenter", () => { reset.style.background = "#2563eb"; reset.style.borderColor = "#2563eb"; reset.style.color = "#fff"; });
        reset.addEventListener("mouseleave", () => { reset.style.background = "#fff"; reset.style.borderColor = "#2563eb"; reset.style.color = "#2563eb"; });
        reset.addEventListener("click", (e) => { e.preventDefault(); e.stopPropagation(); cbRef.current.onReset(acId); });
        ac.insertBefore(reset, ac.firstChild); // before Hide → left-most control
      }
    });

    // ── Migrate cached trash buttons to the styled tooltip. Fresh cards are generated with
    // data-tip (see server newPageCardHeader / bulkMetaData), but cards saved to localStorage
    // before that change still carry a native title="" — move it to data-tip so they match. ──
    el.querySelectorAll<HTMLElement>(".cd-trash-btn[title]").forEach(btn => {
      const t = btn.getAttribute("title");
      if (!t) return;
      btn.setAttribute("data-tip", t);
      if (!btn.getAttribute("aria-label")) btn.setAttribute("aria-label", t);
      btn.removeAttribute("title");
    });

    // ── Internal Links tab: tooltip beside the title explaining the link-insertion
    // logic (per-page builder detection, what builder areas are left untouched, and
    // the Elementor cache regeneration after each link).
    if (id === "internal") {
      const title = el.querySelector<HTMLElement>(".sh h2");
      if (title && !title.querySelector(".audit-tip")) {
        const tip = document.createElement("span");
        tip.className = "audit-tip";
        tip.innerHTML =
          '<span class="audit-tip-icon">?</span>' +
          '<span class="audit-tip-box audit-tip-wide"><strong>How links get added</strong>' +
          "<p>When you Approve a link, the tool checks <strong>that specific page</strong> at the moment of approval to see whether its content lives in the normal WordPress editor or in a page builder (e.g. Elementor). It never assumes from the site-wide builder — the same site can have some pages in Elementor and others in the standard editor, so each page is checked on its own.</p>" +
          "<p>Links are only inserted into <strong>body / paragraph text</strong>. Content rendered by builder <strong>widgets</strong> — navigation, headers/footers, sliders, dynamic or template widgets, shortcodes, etc. — is left untouched, so those areas are not updated.</p>" +
          "<p>On <strong>Elementor</strong> pages the link is written into the matching text widget and Elementor's <strong>CSS cache is regenerated immediately after</strong>, so the new link appears on the live page without a manual cache clear. Classic / Gutenberg pages are edited directly in the page content.</p>" +
          "<p style=\"margin-top:10px;padding-top:9px;border-top:1px solid rgba(255,255,255,.12)\">Implementing internal links requires the <strong>Local SEO Garage (LSG)</strong> plugin — install and activate it to enable one-click link insertion. <span class=\"il-helper-cta\" role=\"button\" tabindex=\"0\">Access the plugin — click here</span></p>" +
          "</span>";
        title.appendChild(tip);
        tip.querySelector(".il-helper-cta")?.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          cbRef.current.onOpenSchemaSetup?.();
        });
      }

      // Tooltip beside the "Key Linking Opportunities" sub-section heading, explaining
      // which pages are eligible as sources, the 50-page cap + priority, and the
      // 3-uses-per-source limit.
      const kloLabel = Array.from(el.querySelectorAll<HTMLElement>(".sub-lbl"))
        .find(n => /key linking opportunities/i.test(n.textContent ?? ""));
      if (kloLabel && !kloLabel.querySelector(".audit-tip")) {
        const tip = document.createElement("span");
        tip.className = "audit-tip";
        tip.innerHTML =
          '<span class="audit-tip-icon">?</span>' +
          '<span class="audit-tip-box audit-tip-wide"><strong>About this list</strong>' +
          "<p>Source pages are chosen carefully: <strong>hub pages</strong> and any page <strong>under 350 words</strong> are not used as source pages for internal links — this is to prevent suggesting adding links from pages with little or no body copy. The one exception is <strong>FAQ pages</strong>, identified by question-style language in the title (e.g. starting with What, How, Why, Can, Is…), whose Q&amp;A text accepts a link.</p>" +
          "<p>This section lists up to <strong>50 of your website's pages</strong> that need internal links, prioritized: <strong>service pages → service-area / location pages → articles / posts → FAQs</strong>.</p>" +
          "<p>Each <strong>source page is used at most 3 times</strong> across the list, so no single page gets stuffed with too many links pointing to other pages.</p>" +
          "</span>";
        kloLabel.appendChild(tip);
      }

      // "View all orphaned pages" reveal button in the Orphaned Pages sub-section: the section
      // renders the first 10 orphan rows and hides the rest (rows tagged .orphan-extra,
      // display:none); clicking reveals them and hides the button.
      el.querySelectorAll<HTMLButtonElement>(".orphan-view-all").forEach(btn => {
        const clone = btn.cloneNode(true) as HTMLButtonElement;
        btn.replaceWith(clone);
        clone.addEventListener("click", (e) => {
          e.preventDefault();
          el.querySelectorAll<HTMLElement>(".orphan-extra").forEach(r => { r.style.display = ""; });
          clone.style.display = "none";
        });
      });

      // "Orphaned Pages — All URLs" table (#orphan-url-list): the URL header sorts the rows
      // alphabetically (toggle A→Z / Z→A), and "Copy All URLs" copies just the URLs.
      el.querySelectorAll<HTMLTableElement>("#orphan-url-list .orphan-url-table").forEach(table => {
        const tbody = table.querySelector("tbody");
        const header = table.querySelector<HTMLElement>(".orphan-url-sort");
        const ind = header?.querySelector<HTMLElement>(".orphan-sort-ind") ?? null;
        if (tbody && header) {
          const clone = header.cloneNode(true) as HTMLElement;
          header.replaceWith(clone);
          const cloneInd = clone.querySelector<HTMLElement>(".orphan-sort-ind") ?? ind;
          const sortRows = () => {
            const dir = clone.dataset.sortDir === "asc" ? "desc" : "asc";
            clone.dataset.sortDir = dir;
            const rows = Array.from(tbody.querySelectorAll<HTMLTableRowElement>("tr"));
            rows.sort((a, b) => {
              const ua = a.getAttribute("data-url") ?? "";
              const ub = b.getAttribute("data-url") ?? "";
              return dir === "asc" ? ua.localeCompare(ub) : ub.localeCompare(ua);
            });
            rows.forEach(r => tbody.appendChild(r));
            if (cloneInd) cloneInd.textContent = dir === "asc" ? "▲" : "▼";
          };
          clone.addEventListener("click", sortRows);
          clone.addEventListener("keydown", (e) => {
            if ((e as KeyboardEvent).key === "Enter" || (e as KeyboardEvent).key === " ") { e.preventDefault(); sortRows(); }
          });
        }
      });
      el.querySelectorAll<HTMLButtonElement>("#orphan-url-list .orphan-copy-urls").forEach(btn => {
        const clone = btn.cloneNode(true) as HTMLButtonElement;
        btn.replaceWith(clone);
        clone.addEventListener("click", async () => {
          const list = clone.closest("#orphan-url-list");
          const urls = Array.from(list?.querySelectorAll<HTMLTableRowElement>(".orphan-url-table tbody tr") ?? [])
            .map(r => r.getAttribute("data-url") ?? "")
            .filter(Boolean);
          if (!urls.length) return;
          const text = urls.join("\n");
          try {
            await navigator.clipboard.writeText(text);
          } catch {
            const ta = document.createElement("textarea");
            ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
            document.body.appendChild(ta); ta.select();
            try { document.execCommand("copy"); } catch { /* ignore */ }
            ta.remove();
          }
          const orig = clone.textContent;
          clone.textContent = "Copied!";
          clone.disabled = true;
          setTimeout(() => { clone.textContent = orig; clone.disabled = false; }, 1500);
        });
      });

      // "Suggest Link" button on the long-tail orphan cards (server-built beyond the AI top ~50):
      // generate ONE internal-link recommendation on demand, then render it as a normal Approve-able
      // .ac block — identical to a top card — so it flows through the existing il- approve pipeline.
      el.querySelectorAll<HTMLButtonElement>(".orphan-suggest").forEach(btn => {
        const clone = btn.cloneNode(true) as HTMLButtonElement;
        btn.replaceWith(clone);
        clone.addEventListener("click", async () => {
          if (clone.disabled) return;
          const orphanUrl = clone.getAttribute("data-orphan-url") ?? "";
          const slug = clone.getAttribute("data-slug") ?? "";
          if (!orphanUrl || !slug) return;
          const anthropicKey = loadAnthropicKey();
          if (!anthropicKey) { void showAlert("Anthropic API key not set. Add it in ⚙ Settings → API Keys.", { title: "API Key Required" }); return; }

          // Candidate source pages from the stored crawl (page inventory + body excerpts).
          let pages: Array<{ url: string; title: string; excerpt: string; wordCount?: number; bodyLinks?: string[] }> = [];
          try {
            const raw = JSON.parse(localStorage.getItem("seo_full_metrics") ?? "null") as { pageMeta?: unknown } | null;
            const pm = Array.isArray(raw?.pageMeta) ? raw!.pageMeta as Array<Record<string, unknown>> : [];
            pages = pm
              .filter(p => typeof p.url === "string" && p.url !== orphanUrl)
              .map(p => ({
                url: p.url as string,
                title: typeof p.title === "string" ? p.title : "",
                excerpt: typeof p.excerpt === "string" ? p.excerpt : "",
                wordCount: typeof p.wordCount === "number" ? p.wordCount : undefined,
                bodyLinks: Array.isArray(p.bodyLinks) ? (p.bodyLinks as unknown[]).filter((x): x is string => typeof x === "string") : undefined,
              }));
          } catch { /* leave empty */ }
          if (!pages.length) { void showAlert("No crawled page data found — run the Internal Links crawl (or Get Competitors) first.", { title: "No Data" }); return; }

          const original = clone.textContent;
          clone.disabled = true;
          clone.textContent = "⏳ Suggesting…";
          const slot = document.getElementById(`orphan-action-${slug}`);
          try {
            const res = await fetch("/api/suggest-internal-link", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                target: orphanUrl, pages, anthropicKey,
                brand: (localStorage.getItem("cover_business_name") ?? "").trim() || undefined,
                location: (localStorage.getItem("cover_location") ?? "").trim() || undefined,
              }),
            });
            const data = await res.json() as { ok: boolean; opportunity?: { fromUrl: string; fromTitle?: string; anchor: string; mode: "existing" | "new"; sentence: string; after: string } | null; error?: string };
            if (!res.ok || !data.ok) { clone.disabled = false; clone.textContent = original; void showAlert(data.error ?? "Suggestion failed.", { title: "Suggest Link" }); return; }
            const op = data.opportunity;
            if (!op) {
              if (slot) slot.innerHTML = '<span style="font-size:11px;color:#94a3b8">No suitable source page found — review manually.</span>';
              return;
            }
            // Fill in the card's oc-action line (the oc-text problem statement stays as-is), so an
            // on-demand card ends up identical to a top-50 card: "→ WordPress action: …".
            const actionEl = document.getElementById(`oc-il-orphan-${slug}`)?.querySelector(".oc-action");
            if (actionEl) {
              // Reference the source page by its URL (not its title), matching the top-50 cards.
              actionEl.textContent = "";
              const lead = document.createElement("strong");
              lead.textContent = "→ WordPress action: ";
              actionEl.appendChild(lead);
              actionEl.appendChild(document.createTextNode("On "));
              const src = document.createElement("a");
              src.href = op.fromUrl;
              src.target = "_blank";
              src.rel = "noopener";
              src.className = "visit-link";
              src.textContent = op.fromUrl;
              actionEl.appendChild(src);
              actionEl.appendChild(document.createTextNode(op.mode === "new"
                ? `, add the sentence “${op.sentence}” — linking “${op.anchor}” to `
                : `, the copy already reads “${op.anchor}” — turn that phrase into a link to `));
              // Destination URL this anchor will link to (this orphan page).
              const dest = document.createElement("a");
              dest.href = orphanUrl;
              dest.target = "_blank";
              dest.rel = "noopener";
              dest.className = "visit-link";
              dest.textContent = orphanUrl;
              actionEl.appendChild(dest);
              actionEl.appendChild(document.createTextNode("."));
            }
            // Render the Approve control block (same markup + data-il-* the top cards use).
            if (slot) {
              slot.innerHTML = "";
              const ac = document.createElement("div");
              ac.className = "ac";
              ac.id = `ac-il-orphan-${slug}`;
              ac.setAttribute("data-il-from", op.fromUrl);
              ac.setAttribute("data-il-to", orphanUrl);
              ac.setAttribute("data-il-anchor", op.anchor);
              ac.setAttribute("data-il-mode", op.mode);
              ac.setAttribute("data-il-sentence", op.sentence ?? "");
              ac.setAttribute("data-il-after", op.after ?? "");
              ac.innerHTML = '<button class="ac-btn">Approve</button><div class="ac-sep"></div><label class="ac-chk"><input type="checkbox"/><span>Ignore</span></label><div class="ac-sep"></div><label class="ac-chk"><input type="checkbox"/><span>✓ Complete</span></label>';
              slot.appendChild(ac);
              // Wire the fresh controls for the first click; the generic pass re-wires on next render.
              const acId = `il-orphan-${slug}`;
              ac.querySelector<HTMLButtonElement>(".ac-btn")?.addEventListener("click", () => cbRef.current.onApprove(acId, undefined, false));
              const boxes = ac.querySelectorAll<HTMLInputElement>('input[type="checkbox"]');
              boxes[0]?.addEventListener("change", (e) => cbRef.current.onIgnore(acId, (e.target as HTMLInputElement).checked));
              boxes[1]?.addEventListener("change", (e) => cbRef.current.onDone(acId, (e.target as HTMLInputElement).checked));
            }
          } catch (err) {
            clone.disabled = false;
            clone.textContent = original;
            void showAlert(`Network error: ${err instanceof Error ? err.message : "request failed"}`, { title: "Suggest Link" });
          }
        });
      });
    }

    // Refined vertical strips (Option 3): strip text emoji/checkmarks from the Ignore/Done labels
    // so the CSS monoline icons (✕ / ✓) are the only icons. Per-card because Internal Links mixes
    // refined Fix Cards with plain Key-Linking controls (see acIsRefined).
    el.querySelectorAll<HTMLElement>(".ac").forEach(ac => {
      if (!acRefined(ac)) return;
      ac.querySelectorAll<HTMLElement>(".ac-chk span").forEach(sp => {
        sp.textContent = (sp.textContent ?? "").replace(/[✓✔☑✅🚫⚡]/gu, "").trim();
      });
    });

    // ── Schema tab: a "Check schema in Google" link under each card's controls ──
    // Opens Google's Rich Results Test in a new tab with the page URL prefilled, so
    // the user can verify the structured data on that exact page after approving.
    if (id === "schema") {
      // "N sites · M types" count on the Schema Types Implemented sub-header (mockup design).
      // Computed from the table: rows = sites, schema-type columns = total header cols minus
      // the leading Site column and the trailing Total column.
      el.querySelectorAll<HTMLElement>(".sub-lbl").forEach(lbl => {
        if (!/schema types implemented/i.test(lbl.textContent ?? "")) return;
        if (lbl.querySelector(".sc-count")) return;
        const table = lbl.closest(".sub")?.querySelector("table");
        if (!table) return;
        const sites = table.querySelectorAll("tbody tr").length;
        const types = Math.max(0, table.querySelectorAll("thead th").length - 2);
        if (!sites || !types) return;
        const span = document.createElement("span");
        span.className = "sc-count";
        span.textContent = `${sites} sites · ${types} types`;
        lbl.appendChild(span);
      });

      // Tooltip after the homepage card title, explaining how the homepage schema is built.
      const homeTitle = el.querySelector<HTMLElement>("#oc-sc-home .oc-title")
        ?? el.querySelector<HTMLElement>("#oc-sc-homepage .oc-title");
      if (homeTitle && !homeTitle.querySelector(".audit-tip")) {
        const tip = document.createElement("span");
        tip.className = "audit-tip tip-left"; // left-anchored so the box can't clip off the left edge
        tip.innerHTML =
          '<span class="audit-tip-icon">?</span>' +
          '<span class="audit-tip-box" style="width:340px"><strong>How the homepage schema is built</strong>' +
          "<p>It's assembled live from your Google Business Profile (category, description, rating &amp; Google Map), the service pages and service areas pulled from your website, and the social/citation profiles found via a live Google search for your domain.</p>" +
          "<p>If your site has no Google Business Profile (or it can't be matched), the schema is built only from your website's data and an SEO-optimized business description is generated automatically.</p>" +
          '<p>Approve to inject it into the page &lt;head&gt;. Review or edit any of it afterward in the Schema box in the page editor.</p>' +
          '</span>';
        homeTitle.appendChild(tip);
      }

      // Tooltip next to the section title explaining how the suggested schema works + how to verify it.
      const schemaTitle = el.querySelector<HTMLElement>(".sh h2");
      if (schemaTitle && !schemaTitle.querySelector(".audit-tip")) {
        const tip = document.createElement("span");
        tip.className = "audit-tip tip-left"; // left-anchored so the wide box can't clip off the left edge
        tip.innerHTML =
          '<span class="audit-tip-icon">?</span>' +
          '<span class="audit-tip-box" style="width:520px"><strong>About the suggested schema</strong>' +
          "<p>Each scan surfaces up to <strong>50 pages</strong> that need schema (the site crawl reads far more, but only 50 are shown per scan), chosen by importance in this order: <strong>homepage → core service pages → high-intent / conversion pages → business-entity pages (about, staff, certifications) → supporting content → blog / articles</strong>. Once a page has complete schema it won't appear again — a later scan surfaces the next 50 pages instead. To add schema to a <strong>specific page URL</strong>, use the <strong>Express Schema</strong> tab.</p>" +
          "<p>Suggested schema is <strong>additive</strong> — on pages that already have schema, only the missing types are added, so existing markup is never duplicated or overwritten.</p>" +
          "<p>Every suggestion is run through the <strong>schema.org vocabulary</strong> to validate its types, properties, and values for accuracy before it's added.</p>" +
          "<p>To check what's actually live on a page, use the <strong>schema.org validator</strong> — it reports all structured data present. Google's Structured Data / Rich Results tool only reads schema that qualifies for rich snippets, so valid schema that isn't rich-eligible won't show there even though it's live in your page's source code.</p>" +
          "<p><strong>Note:</strong> caching on your website (or CDN) can affect what validator tools are able to verify — purge the page cache after approving so the tools fetch the latest version.</p>" +
          '</span>';
        schemaTitle.appendChild(tip);
      }
    }

    // Check-schema buttons (Google Rich Results + schema.org validator) under each schema card's
    // controls — on the Schema tab (all cards) and on the NLP Entities tab (only cards that carry a
    // schema payload, i.e. the entity fixes that inject JSON-LD on approve).
    if (id === "schema" || id === "entities") {
      el.querySelectorAll<HTMLElement>(".ac").forEach(ac => {
        const card = ac.closest(".oc") as HTMLElement | null;
        if (!card || card.querySelector(".ac-schema-check")) return; // none, or already added
        if (id === "entities" && !card.querySelector(".schema-payload")) return; // Entities: schema cards only

        // Resolve the page URL: prefer the machine-readable payload, fall back to the visit link.
        let pageUrl = "";
        const payload = card.querySelector(".schema-payload")?.textContent?.trim();
        if (payload) { try { const p = JSON.parse(payload) as { url?: unknown }; if (typeof p.url === "string") pageUrl = p.url; } catch { /* ignore */ } }
        if (!pageUrl) pageUrl = card.querySelector<HTMLAnchorElement>(".visit-link")?.getAttribute("href") ?? "";
        if (!pageUrl) return;

        // Two "check schema" buttons on ONE row, splitting the strip width 50/50 (flex:1 each).
        const checkBtnCss =
          "display:flex;align-items:center;justify-content:center;flex:1 1 0;min-width:0;box-sizing:border-box;height:28px;padding:0 6px;" +
          "font-family:'Inter','Mulish',sans-serif;font-size:10px;font-weight:700;letter-spacing:.1px;" +
          "color:#1d4ed8;background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;" + // light blue (external validators); 8px corners match the Approve/Done/Hide buttons above
          "text-decoration:none;white-space:nowrap;cursor:pointer;transition:background .15s;";
        const hoverBtn = (a: HTMLElement) => {
          a.addEventListener("mouseenter", () => { a.style.background = "#dbeafe"; });
          a.addEventListener("mouseleave", () => { a.style.background = "#eff6ff"; });
        };

        const checkRow = document.createElement("div");
        checkRow.className = "ac-schema-check-row";
        checkRow.style.cssText = "display:flex;gap:6px;width:100%;box-sizing:border-box;margin-top:10px;";

        const link = document.createElement("a");
        link.className = "ac-schema-check";
        link.href = `https://search.google.com/test/rich-results?url=${encodeURIComponent(pageUrl)}`;
        link.target = "_blank";
        link.rel = "noopener";
        link.textContent = "Check in Google";
        link.setAttribute("data-tip", "Open Google's Rich Results Test for this page in a new tab (URL prefilled)");
        link.style.cssText = checkBtnCss;
        hoverBtn(link);

        // "Check on Schema.org" — opens validator.schema.org with the URL prefilled, which
        // fetches the page and runs the validator automatically.
        const link2 = document.createElement("a");
        link2.className = "ac-schema-check-org";
        link2.href = `https://validator.schema.org/#url=${encodeURIComponent(pageUrl)}`;
        link2.target = "_blank";
        link2.rel = "noopener";
        link2.textContent = "Check Schema.org";
        link2.setAttribute("data-tip", "Open the schema.org validator for this page in a new tab — it loads the URL and runs the validator automatically");
        link2.style.cssText = checkBtnCss;
        hoverBtn(link2);

        checkRow.appendChild(link);
        checkRow.appendChild(link2);
        ac.insertAdjacentElement("afterend", checkRow);

        // Run the schema.org conformance check on THIS card's blocks and append the result INTO the
        // card's action sentence (after the "…<head>." part) — so the confirmation reads inline with
        // what Approve does, rather than sitting in the control strip.
        if (payload) {
          const ocAction = card.querySelector<HTMLElement>(".oc-action");
          if (ocAction && !ocAction.querySelector(".oc-schema-valid")) {
            const tag = document.createElement("span");
            tag.className = "oc-schema-valid";
            tag.style.cssText = "margin-left:6px;font-weight:700;color:#16a34a;";
            ocAction.appendChild(tag);
            void (async () => {
              try {
                const p = JSON.parse(payload) as unknown;
                const blocks = Array.isArray(p) ? p : Array.isArray((p as { blocks?: unknown[] }).blocks) ? (p as { blocks: unknown[] }).blocks : [];
                if (!blocks.length) { tag.remove(); return; }
                const res = await fetch("/api/validate-schema", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ blocks }) });
                const d = await res.json() as { ok?: boolean; errorCount?: number; issues?: { severity: string; message: string }[] };
                if (!d.ok) { tag.remove(); return; }
                const errs = d.errorCount ?? 0;
                if (errs === 0) {
                  tag.textContent = "Suggested schema validated (schema.org).";
                } else {
                  // The issue count is a link that opens the full list in a (wide) modal — no hover
                  // tooltip, so the complete text is never clipped by a fixed-width popup.
                  tag.style.color = "#d97706";
                  const errList = (d.issues ?? []).filter(i => i.severity === "error").map(i => i.message);
                  tag.appendChild(document.createTextNode("Suggested schema validated (schema.org) — "));
                  const countLink = document.createElement("a");
                  countLink.href = "#";
                  countLink.className = "oc-schema-issues-link";
                  countLink.textContent = `${errs} issue${errs === 1 ? "" : "s"}`;
                  countLink.style.cssText = "color:#d97706;font-weight:700;text-decoration:underline;cursor:pointer;";
                  countLink.addEventListener("click", e => {
                    e.preventDefault();
                    cbRef.current.onViewIssues?.(errList, pageUrl);
                  });
                  tag.appendChild(countLink);
                  tag.appendChild(document.createTextNode(" auto-fixed on approve."));
                }
              } catch { tag.remove(); }
            })();
          }

          // "View JSON-LD schema" — a link at the end of the action sentence that opens the exact
          // (validated) schema this card would add, in a copy popup, so it can be grabbed and pasted /
          // replaced on the page manually when needed.
          if (ocAction && !ocAction.querySelector(".oc-view-schema") && cbRef.current.onViewSchema) {
            const vlink = document.createElement("a");
            vlink.className = "oc-view-schema";
            vlink.href = "#";
            vlink.textContent = "View JSON-LD schema";
            vlink.style.cssText = "margin-left:8px;font-weight:700;color:#2563eb;text-decoration:underline;cursor:pointer;white-space:nowrap;";
            vlink.addEventListener("click", (e) => {
              e.preventDefault();
              void (async () => {
                let blocks: unknown[] = [];
                try { const pj = JSON.parse(payload) as unknown; blocks = Array.isArray(pj) ? pj : Array.isArray((pj as { blocks?: unknown[] }).blocks) ? (pj as { blocks: unknown[] }).blocks : []; } catch { /* ignore */ }
                let out: unknown = blocks;
                // Show exactly what Approve would write — run the blocks through the validator first.
                try {
                  const res = await fetch("/api/validate-schema", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ blocks }) });
                  const d = await res.json() as { ok?: boolean; blocks?: unknown[] };
                  if (d.ok && Array.isArray(d.blocks)) out = d.blocks;
                } catch { /* fall back to the raw payload blocks */ }
                cbRef.current.onViewSchema?.(JSON.stringify(out, null, 2), pageUrl);
              })();
            });
            ocAction.appendChild(vlink);
          }
        }

      });
    }

    // ── On-Page tab: make each card's page URL a clickable "↗" visit link (new tab) ──
    // Newly-regenerated cards get this from enrichOnPagePayloads, but older cards don't — add it
    // client-side from the op-payload URL so existing cards are clickable without a recrawl.
    if (id === "onpage") {
      el.querySelectorAll<HTMLElement>(".oc").forEach(card => {
        if (card.querySelector(".visit-link")) return; // already has one
        const payload = card.querySelector(".op-payload")?.textContent?.trim();
        if (!payload) return;
        let url = "";
        try { const p = JSON.parse(payload) as { url?: unknown }; if (typeof p.url === "string") url = p.url.trim(); } catch { return; }
        if (!/^https?:\/\//i.test(url)) return; // blank/unmatched card → nothing to link
        const link = document.createElement("a");
        link.href = url; link.target = "_blank"; link.rel = "noopener"; link.className = "visit-link";
        link.textContent = `${url} ↗`;
        const cat = card.querySelector(".oc-cat");
        if (cat) cat.insertAdjacentElement("afterend", link);
        else card.querySelector(".oc-hd")?.appendChild(link);
      });
    }

    // NLP Entities cards target a specific page — add a clickable "↗ Visit" link (opens in a new
    // tab) from the card's schema-payload URL when the header doesn't already have one.
    if (id === "entities") {
      el.querySelectorAll<HTMLElement>(".oc").forEach(card => {
        if (card.querySelector(".visit-link")) return; // already has one (from the generator)
        const payload = card.querySelector(".schema-payload")?.textContent?.trim();
        if (!payload) return;
        let url = "";
        try { const p = JSON.parse(payload) as { url?: unknown }; if (typeof p.url === "string") url = p.url.trim(); } catch { return; }
        if (!/^https?:\/\//i.test(url)) return;
        const link = document.createElement("a");
        link.href = url; link.target = "_blank"; link.rel = "noopener"; link.className = "visit-link";
        link.textContent = "↗ Visit";
        const cat = card.querySelector(".oc-cat");
        if (cat) cat.insertAdjacentElement("afterend", link);
        else card.querySelector(".oc-hd")?.appendChild(link);
      });
    }

    // Internal Links: a tooltip placed inside the score table's <th> gets clipped by the .tw
    // overflow — relocate it next to the sub-section label (outside .tw) so it pops freely.
    if (id === "internal") {
      const tip = el.querySelector<HTMLElement>(".tw th .audit-tip");
      const lbl = tip?.closest(".sub")?.querySelector<HTMLElement>(".sub-lbl");
      if (tip && lbl && !lbl.querySelector(".audit-tip")) {
        lbl.appendChild(document.createTextNode(" "));
        lbl.appendChild(tip);
      }
    }

    // ── Wire ignore checkboxes ───────────────────────────────
    el.querySelectorAll<HTMLInputElement>(".ac-chk input[type=checkbox]").forEach(cb => {
      const ac = cb.closest(".ac") as HTMLElement | null;
      if (!ac) return;
      const acId = ac.id?.replace(/^ac-/, "");
      if (!acId) return;
      const label = cb.closest(".ac-chk")?.querySelector("span")?.textContent ?? "";
      const isDoneBox = label.includes("✓");

      const clone = cb.cloneNode(true) as HTMLInputElement;
      cb.replaceWith(clone);

      clone.addEventListener("change", () => {
        if (isDoneBox) {
          if (!clone.checked) {
            // Unchecking Done — require confirmation before clearing.
            // showConfirm is async, so revert the visual uncheck up front and
            // re-apply only once the user confirms.
            clone.checked = true;
            void showConfirm("Confirm you want to mark this task as not complete.", {
              title: "Mark Task Incomplete",
              confirmLabel: "Mark Incomplete",
              tone: "danger",
            }).then(confirmed => {
              if (!confirmed) return;
              clone.checked = false;
              cbRef.current.onDone(acId, false);
            });
            return;
          }
          cbRef.current.onDone(acId, clone.checked);
        } else {
          cbRef.current.onIgnore(acId, clone.checked);
        }
      });
    });

    // ── Wire FAQ toggles ─────────────────────────────────────
    el.querySelectorAll<HTMLElement>(".faq-q").forEach(q => {
      const item = q.closest(".faq-item") as HTMLElement | null;
      if (!item) return;
      const clone = q.cloneNode(true) as HTMLElement;
      q.replaceWith(clone);
      clone.addEventListener("click", () => item.classList.toggle("open"));
    });

    // ── Wire create-expand buttons (show/hide form row) ──────
    el.querySelectorAll<HTMLButtonElement>(".create-expand-btn").forEach(btn => {
      const clone = btn.cloneNode(true) as HTMLButtonElement;
      btn.replaceWith(clone);
      clone.addEventListener("click", (e) => {
        // The Create button can sit inside a .faq-q (FAQs tab) — don't also toggle the accordion.
        e.stopPropagation();
        const target = clone.getAttribute("data-target");
        if (!target) return;
        const row = el.querySelector<HTMLElement>(`#${target}`);
        if (!row) return;
        const showing = row.style.display === "none";
        row.style.display = showing ? "" : "none";
        // Bulk Content Drawer cards carry data-autogen="1": auto-run content generation
        // the first time the card is opened, so the editor appears already filled.
        if (showing && clone.getAttribute("data-autogen") === "1" && clone.dataset.autogenDone !== "1") {
          const fixId = target.replace(/^cfr-/, "");
          const editor = el.querySelector<HTMLElement>(`#cft-content-${fixId}`);
          const genBtn = el.querySelector<HTMLButtonElement>(`.generate-content-btn[data-fix-id="${fixId}"]`);
          if (genBtn && editor && !editor.textContent?.trim()) {
            clone.dataset.autogenDone = "1";
            genBtn.click();
          }
        }
      });
    });

    // ── Wire create-submit buttons ───────────────────────────
    el.querySelectorAll<HTMLButtonElement>(".create-submit-btn").forEach(btn => {
      const clone = btn.cloneNode(true) as HTMLButtonElement;
      btn.replaceWith(clone);
      clone.addEventListener("click", () => {
        const fixId = clone.getAttribute("data-fix-id");
        if (!fixId) return;
        // Use the current (possibly user-edited) Page Title for the confirm dialog.
        const titleVal = (document.getElementById(`cft-title-${fixId}`) as HTMLInputElement | null)?.value?.trim();
        const msg = titleVal || clone.getAttribute("data-confirm-msg") || `fix: ${fixId}`;
        cbRef.current.onApprove(fixId, msg, true);
      });
    });

    // ── Wire create-cancel buttons ───────────────────────────
    el.querySelectorAll<HTMLButtonElement>(".create-cancel-btn").forEach(btn => {
      const clone = btn.cloneNode(true) as HTMLButtonElement;
      btn.replaceWith(clone);
      clone.addEventListener("click", () => {
        const target = clone.getAttribute("data-target");
        if (!target) return;
        const row = el.querySelector<HTMLElement>(`#${target}`);
        if (row) row.style.display = "none";
      });
    });

    // ── WYSIWYG editor (Generated Content): rich-text contenteditable + toolbar ──
    // The editor shows the content rendered (headings/bold/links/lists) exactly as it
    // will look on the page; its HTML is what gets created in WordPress.
    el.querySelectorAll<HTMLElement>(".rt-toolbar").forEach(tb => {
      const fixId = tb.getAttribute("data-fix-id");
      if (!fixId) return;
      const ed = el.querySelector<HTMLElement>(`#cft-content-${fixId}`);
      if (!ed || ed.dataset.rtWired === "1") return;
      ed.dataset.rtWired = "1";
      const exec = (cmd: string, value?: string) => { ed.focus(); document.execCommand(cmd, false, value); };

      tb.querySelector<HTMLSelectElement>(".rt-tb-heading")?.addEventListener("change", (e) => {
        const sel = e.currentTarget as HTMLSelectElement;
        exec("formatBlock", sel.value === "P" ? "P" : sel.value); // e.g. "H2"
        // No reset — the selection tracker below keeps the dropdown showing the caret's block level.
      });
      tb.querySelectorAll<HTMLButtonElement>(".rt-tb-btn").forEach(b => {
        b.addEventListener("click", async (e) => {
          e.preventDefault();
          const kind = b.getAttribute("data-rt");
          if (kind === "bold") exec("bold");
          else if (kind === "italic") exec("italic");
          else if (kind === "underline") exec("underline");
          else if (kind === "alignLeft") exec("justifyLeft");
          else if (kind === "alignCenter") exec("justifyCenter");
          else if (kind === "alignRight") exec("justifyRight");
          else if (kind === "link") {
            // Capture the editor selection before the modal steals focus, then restore
            // it so execCommand("createLink") applies to the originally selected text.
            const selBefore = window.getSelection();
            const savedRange = selBefore && selBefore.rangeCount ? selBefore.getRangeAt(0).cloneRange() : null;
            const url = await showPrompt("Enter the URL to link to:", {
              title: "Add Link", tone: "info",
              placeholder: "https://example.com/page/", defaultValue: "https://",
              confirmLabel: "Add Link",
            });
            if (url) {
              ed.focus();
              if (savedRange) {
                const sel = window.getSelection();
                sel?.removeAllRanges();
                sel?.addRange(savedRange);
              }
              document.execCommand("createLink", false, url);
            }
          }
          else if (kind === "ul") exec("insertUnorderedList");
        });
      });

      // Text-color picker: apply foreColor to the selection. The native picker steals
      // focus, so capture the selection on mousedown and restore it before applying.
      const colorInput = tb.querySelector<HTMLInputElement>(".rt-tb-color");
      if (colorInput) {
        let colorRange: Range | null = null;
        colorInput.addEventListener("mousedown", () => {
          const sel = window.getSelection();
          colorRange = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
        });
        colorInput.addEventListener("input", () => {
          ed.focus();
          if (colorRange) {
            const sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(colorRange);
          }
          // styleWithCSS → emits <span style="color:…"> (preserved by WordPress) rather than <font>.
          document.execCommand("styleWithCSS", false, "true");
          document.execCommand("foreColor", false, colorInput.value);
          document.execCommand("styleWithCSS", false, "false");
        });
      }

      // Links inside the editor: a plain click opens an editor for the link's URL (clear the
      // field to remove the link, keeping its text); Ctrl/Cmd+click opens the href in a new tab.
      ed.addEventListener("click", (e) => {
        const a = (e.target as HTMLElement).closest("a");
        if (!a || !a.getAttribute("href")) return;
        if (e.metaKey || e.ctrlKey) {
          e.preventDefault();
          window.open(a.href, "_blank", "noopener,noreferrer");
          return;
        }
        e.preventDefault();
        const current = a.getAttribute("href") ?? "";
        void (async () => {
          const url = await showPrompt("Edit the link URL (clear the field and save to remove the link, keeping its text):", {
            title: "Edit Link", tone: "info",
            placeholder: "https://example.com/page/", defaultValue: current, confirmLabel: "Save Link",
          });
          if (url === null) return; // cancelled — leave the link unchanged
          const trimmed = url.trim();
          if (!trimmed) {
            a.replaceWith(document.createTextNode(a.textContent ?? "")); // unwrap → keep text, drop link
          } else {
            a.setAttribute("href", trimmed);
          }
          ed.dispatchEvent(new Event("input", { bubbles: true })); // persist the change to the draft
        })();
      });
      // Hint the click affordances on hover.
      ed.querySelectorAll<HTMLAnchorElement>("a[href]").forEach(a => {
        a.title = "Click to edit this link · Ctrl/Cmd+click to open in a new tab";
      });

      // ── Draft persistence: save generated content + field edits, and restore them after
      //    a re-render (tab switch / reload) so the user never loses generated content. ──
      const saveDraft = () => patchCreateDraft(fixId, {
        content: ed.innerHTML,
        fields: captureCreateFields(fixId),
        faqAnswer: ed.dataset.faqAnswer || undefined,
      });
      ed.addEventListener("input", () => scheduleDraftSave(fixId, saveDraft));
      const panel = ed.closest(".create-form-panel");
      if (panel && !panel.getAttribute("data-draft-wired")) {
        panel.setAttribute("data-draft-wired", "1");
        panel.addEventListener("input", () => scheduleDraftSave(fixId, saveDraft));
      }

      // Restore a saved draft into the freshly re-rendered form.
      const draft = getCreateDraft(fixId);
      if (draft) {
        if (draft.fields) {
          for (const [k, v] of Object.entries(draft.fields)) {
            const fld = el.querySelector(`#cft-${k}-${fixId}`) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
            if (!fld || v == null) continue;
            if (fld instanceof HTMLInputElement && fld.type === "checkbox") {
              // Restore the summarization toggle + reveal its heading field to match.
              fld.checked = v === "1";
              const summWrap = el.querySelector<HTMLElement>(`#cft-summaryheading-wrap-${fixId}`);
              if (summWrap) summWrap.style.display = fld.checked ? "block" : "none";
            } else if (fld.value !== v) {
              fld.value = v;
            }
          }
        }
        if (draft.content && draft.content.trim()) {
          ed.innerHTML = draft.content;
          ed.querySelectorAll<HTMLAnchorElement>("a[href]").forEach(a => { a.title = "Click to edit this link · Ctrl/Cmd+click to open in a new tab"; });
          if (draft.faqAnswer) ed.dataset.faqAnswer = draft.faqAnswer; else delete ed.dataset.faqAnswer;
          const wrap = el.querySelector<HTMLElement>(`#cfp-content-wrap-${fixId}`);
          if (wrap) wrap.style.display = "";
          const submit = el.querySelector<HTMLButtonElement>(`.create-submit-btn[data-fix-id="${fixId}"]`);
          if (submit) submit.style.display = "";
        }
      } else {
        // Fresh form (no draft yet) → prefill the Custom Instructions box with this site's
        // enabled Global Instructions. Only when empty, and only for a never-touched form, so a
        // user who clears them on a page (which creates a draft) isn't re-prefilled.
        const ciEl = el.querySelector<HTMLTextAreaElement>(`#cft-ci-${fixId}`);
        if (ciEl && !ciEl.value.trim()) {
          try {
            if (localStorage.getItem("seo_global_instructions_enabled") === "true") {
              const gi = (localStorage.getItem("seo_global_instructions") ?? "").trim();
              if (gi) ciEl.value = gi;
            }
          } catch { /* ignore */ }
        }
      }
    });

    // ── Heading-level tracker (WYSIWYG editors) ──────────────────────────────
    // While the caret/selection sits inside a rich-text editor: reflect the current block level in
    // that editor's Heading dropdown (so H2 shows selected for an H2), and show a floating "H2/H3…"
    // badge above the heading. One document listener, replaced whenever a section re-wires; only
    // active when this section actually has editors.
    if (rtSelectionTracker) { document.removeEventListener("selectionchange", rtSelectionTracker); rtSelectionTracker = null; }
    if (rtHeadingBadge) rtHeadingBadge.style.display = "none";
    if (el.querySelector(".rt-toolbar")) {
      rtSelectionTracker = () => {
        const badge = rtGetHeadingBadge();
        const selc = window.getSelection();
        const anchor = selc && selc.rangeCount ? selc.anchorNode : null;
        // The .rt-editor that contains the caret, if any.
        let editor: HTMLElement | null = null;
        for (let n: Node | null = anchor; n; n = n.parentNode) {
          if (n.nodeType === 1 && (n as HTMLElement).classList?.contains("rt-editor")) { editor = n as HTMLElement; break; }
        }
        if (!editor || !anchor) { badge.style.display = "none"; return; }
        // Nearest block element + its tag.
        let blockEl: HTMLElement | null = null; let tag = "P";
        for (let n: Node | null = anchor; n && n !== editor; n = n.parentNode) {
          if (n.nodeType === 1) { const t = (n as HTMLElement).tagName; if (/^(H[1-6]|P|LI)$/.test(t)) { blockEl = n as HTMLElement; tag = t; break; } }
        }
        // Reflect the block level in THIS editor's Heading dropdown.
        const editorFixId = editor.id.replace(/^cft-content-/, "");
        const dd = document.querySelector<HTMLSelectElement>(`.rt-toolbar[data-fix-id="${editorFixId}"] .rt-tb-heading`);
        if (dd) dd.value = /^H[1-6]$/.test(tag) ? tag : "P";
        // Floating badge only when the caret is inside a heading.
        if (blockEl && /^H[1-6]$/.test(tag)) {
          const r = blockEl.getBoundingClientRect();
          badge.textContent = tag;
          badge.style.display = "block";
          badge.style.left = `${Math.round(r.left)}px`;
          badge.style.top = `${Math.round(Math.max(4, r.top - 24))}px`;
        } else {
          badge.style.display = "none";
        }
      };
      document.addEventListener("selectionchange", rtSelectionTracker);
    }

    // ── Post/Page selector controls the dual-purpose field below it ──
    // Page → "Parent Page" (hierarchical nesting); Post → "Enter Post Category" (files the
    // post under a category). Swap the label + placeholder live when the type changes.
    el.querySelectorAll<HTMLSelectElement>('select[id^="cft-type-"]').forEach(sel => {
      const fixId = sel.id.replace(/^cft-type-/, "");
      const parInput = el.querySelector<HTMLInputElement>(`#cft-par-${fixId}`);
      if (!parInput) return;
      const label = parInput.closest(".cfp-field")?.querySelector(".cfp-label") as HTMLElement | null;
      // The "Page Tags" field also relabels to "Post Tags" for posts. Keep the trailing
      // "(comma delimited)" span — only swap the leading text node.
      const ptInput = el.querySelector<HTMLInputElement>(`#cft-pt-${fixId}`);
      const ptLabel = ptInput?.closest(".cfp-field")?.querySelector(".cfp-label") as HTMLElement | null;
      const ptTextNode = ptLabel ? Array.from(ptLabel.childNodes).find(n => n.nodeType === Node.TEXT_NODE && (n.textContent ?? "").trim()) : undefined;

      // Add a "Search Parent" / "Search Post Category" button to the right of the field, shortening
      // the input to make room. Clicking it opens a picker that fills this field.
      if (!parInput.dataset.parentBtnWired) {
        parInput.dataset.parentBtnWired = "1";
        const row = document.createElement("div");
        row.style.display = "flex";
        row.style.gap = "6px";
        row.style.alignItems = "stretch";
        parInput.insertAdjacentElement("beforebegin", row);
        row.appendChild(parInput);
        parInput.style.flex = "1";
        parInput.style.minWidth = "0";
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "cfp-parent-search-btn";
        btn.style.cssText = "flex:0 0 auto;padding:0 10px;background:#2563eb;color:#fff;border:none;border-radius:0;font-size:11px;font-weight:700;cursor:pointer;white-space:nowrap;font-family:'Inter','Mulish',sans-serif;";
        btn.addEventListener("click", () => {
          if (!getClient?.()) { void showAlert("Connect to your website first to pull the pages and categories.", { title: "Not Connected" }); return; }
          setParentPicker({ fixId, mode: sel.value === "post" ? "post" : "page" });
        });
        row.appendChild(btn);
      }

      const applyTypeUI = () => {
        const isPost = sel.value === "post";
        // innerHTML (static, no user input) so the "(slug or blank for root)" hint keeps its grey style.
        if (label) label.innerHTML = isPost ? "Enter Post Category" : `Parent Page <span style="font-weight:400;text-transform:none;color:#94a3b8">(slug or blank for root)</span>`;
        parInput.placeholder = isPost ? "Category name (created if it doesn't exist)" : "Parent page slug or URL (blank = root)";
        if (ptTextNode) ptTextNode.textContent = isPost ? "Post Tags " : "Page Tags ";
        const searchBtn = parInput.closest(".cfp-field")?.querySelector<HTMLButtonElement>(".cfp-parent-search-btn");
        if (searchBtn) searchBtn.textContent = isPost ? "Search Post Category" : "Search Parent";
      };

      // Per-site optional DEFAULTS (Settings → Global Instructions) prefill the Tags field
      // (Default Page/Post Tags) and, for posts, the Post Category field. Only for current
      // standard forms, never Service Area cards (they get their own SA defaults), and only when
      // the field is empty or still holds a previously-applied default — never clobber a user edit.
      const isStd = !!parInput.closest(".create-form-panel")?.hasAttribute("data-cfp-std");
      const isSA = fixId.startsWith("sa-");
      const gd = (k: string) => { try { return (localStorage.getItem(k) ?? "").trim(); } catch { return ""; } };
      const applyTagDefault = (isPost: boolean) => {
        if (!isStd || isSA || !ptInput) return;
        const want = isPost ? gd("seo_default_post_tags") : gd("seo_default_page_tags");
        const prev = ptInput.dataset.appliedDefault ?? "";
        if (ptInput.value.trim() === "" || ptInput.value === prev) { ptInput.value = want; ptInput.dataset.appliedDefault = want; }
      };
      const applyCatDefault = (isPost: boolean) => {
        if (!isStd || isSA) return;
        const prev = parInput.dataset.appliedCat ?? "";
        if (isPost) {
          const want = gd("seo_default_post_category");
          if (parInput.value.trim() === "" || parInput.value === prev) { parInput.value = want; parInput.dataset.appliedCat = want; }
        } else if (prev && parInput.value === prev) {
          parInput.value = ""; parInput.dataset.appliedCat = ""; // clear the post-category default when switching to Page
        }
      };

      applyTypeUI();
      if (!sel.dataset.typeUiWired) {
        sel.dataset.typeUiWired = "1";
        sel.addEventListener("change", () => { applyTypeUI(); applyTagDefault(sel.value === "post"); applyCatDefault(sel.value === "post"); });
      }
      if (isStd && !isSA && !sel.dataset.defaultsInit) {
        sel.dataset.defaultsInit = "1";
        applyTagDefault(sel.value === "post");
        applyCatDefault(sel.value === "post");
      }
    });

    // ── Place the Cancel button to the right of the Generate button ──
    // (older cached form markup has Cancel down in the actions row next to Publish).
    el.querySelectorAll<HTMLButtonElement>(".generate-content-btn").forEach(genBtn => {
      const fixId = genBtn.getAttribute("data-fix-id");
      const area = genBtn.closest(".cfp-generate-area");
      if (!fixId || !area) return;
      const cancelBtn = el.querySelector<HTMLButtonElement>(`.create-cancel-btn[data-target="cfr-${fixId}"]`);
      if (cancelBtn && cancelBtn.parentElement !== area) {
        genBtn.insertAdjacentElement("afterend", cancelBtn);
      }
    });

    // ── Wire generate-content buttons ────────────────────────
    el.querySelectorAll<HTMLButtonElement>(".generate-content-btn").forEach(btn => {
      const clone = btn.cloneNode(true) as HTMLButtonElement;
      // Drop the leading 🤖 icon from older cached markup.
      clone.textContent = (clone.textContent ?? "").replace(/^\s*🤖\s*/, "");
      btn.replaceWith(clone);
      // Per-button abort controller: while a crawl/generation is in flight the button becomes a red
      // "Stop Crawling" and clicking it aborts the request.
      let genAbort: AbortController | null = null;
      clone.addEventListener("click", async () => {
        // A click while crawling cancels the in-flight request instead of starting a new one.
        if (genAbort) { genAbort.abort(); return; }
        const fixId = clone.getAttribute("data-fix-id");
        if (!fixId) return;

        const getVal = (id: string) =>
          ((document.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | null)?.value ?? "").trim();

        const keyword = getVal(`cft-kw-${fixId}`);
        if (!keyword) { void showAlert("Fill in the Primary Keyword field before generating.", { title: "Missing Keyword" }); return; }

        const anthropicKey = loadAnthropicKey();
        if (!anthropicKey) {
          void showAlert("Anthropic API key not set.\n\nGo to ⚙ Settings and add your Anthropic API key (sk-ant-…) to enable content generation.", { title: "API Key Required" });
          return;
        }

        const statusEl  = document.getElementById(`gen-status-${fixId}`) as HTMLElement | null;
        const contentEl = document.getElementById(`cft-content-${fixId}`) as HTMLElement | null;
        const wrapEl    = document.getElementById(`cfp-content-wrap-${fixId}`) as HTMLElement | null;

        const setStatus = (msg: string, color = "#475569", boxed = false, loading = false) => {
          if (!statusEl) return;
          statusEl.style.display = "block";
          statusEl.style.color = color;
          statusEl.textContent = msg;
          // A spinning circle (CSS ::before) while a step is in progress (e.g. the SERP crawl).
          statusEl.classList.toggle("gen-loading", loading);
          // The success summary gets a solid-blue box (the Run Full Audit button's blue) with white
          // text + a slightly larger font so it stands out; every other (plain) status clears it.
          if (boxed) {
            statusEl.style.background = "#0284c7";
            statusEl.style.border = "1px solid #0284c7";
            statusEl.style.borderRadius = "6px";
            statusEl.style.padding = "8px 12px";
            statusEl.style.fontSize = "12px"; // +1px over the base 11px
            statusEl.style.color = "#fff"; // white text on the blue box
          } else {
            statusEl.style.background = "";
            statusEl.style.border = "";
            statusEl.style.borderRadius = "";
            statusEl.style.padding = "";
            statusEl.style.fontSize = "";
          }
        };

        // Enter the crawling state: a clickable red "Stop Crawling" button (NOT disabled, so it can
        // cancel). Remember the prior label so a cancel restores it (vs. "Regenerate" only on success).
        const originalLabel = clone.textContent || "Generate Optimized Content";
        let succeeded = false;
        genAbort = new AbortController();
        clone.disabled = false;
        clone.textContent = "Stop Crawling";
        clone.classList.add("gen-stop");
        setStatus("Fetching top SERP results + competitor pages… (15–30 sec)", "#475569", false, true);

        try {
          const tags = getVal(`cft-pt-${fixId}`);
          // Client-agnostic context — brand, location, and this site's real pages
          // (for internal links) come from the connected site, never hardcoded.
          const brand    = (localStorage.getItem("cover_business_name") ?? "").trim();
          const location = (localStorage.getItem("cover_location") ?? "").trim();
          // Site origin for building absolute internal-link URLs.
          const siteUrl = (() => {
            const raw = (localStorage.getItem("cover_website_url") ?? "").trim();
            try { return new URL(raw.startsWith("http") ? raw : `https://${raw}`).origin; }
            catch { return ""; }
          })();
          // Pages (URL + title) for topic-matched internal links. Titles come from the
          // crawl (pageMeta) so the generator can pick relevant pages by title, not just slug.
          let internalLinkPages = (() => {
            try {
              const fm = JSON.parse(localStorage.getItem("seo_full_metrics") ?? "null") as
                { pageMeta?: Array<{ url?: string; title?: string; error?: string }>; allSitemapUrls?: string[]; topPages?: Array<{ url?: string }> } | null;
              const meta = (fm?.pageMeta ?? [])
                .filter(p => p?.url && !p.error)
                .map(p => ({ url: p.url as string, title: (p.title ?? "").trim() }));
              const base = meta.length
                ? meta
                : (fm?.allSitemapUrls ?? (fm?.topPages ?? []).map(p => p.url ?? "")).map(u => ({ url: u, title: "" }));
              return base
                .map(p => { try { return { url: new URL(p.url, siteUrl || undefined).href, title: p.title }; } catch { return null; } })
                .filter((p): p is { url: string; title: string } => {
                  if (!p) return false;
                  try { return new URL(p.url).pathname !== "/"; } catch { return false; }
                })
                .filter((p, i, arr) => arr.findIndex(q => q.url === p.url) === i)
                .slice(0, 500); // large candidate pool; the server excludes service-area pages, then keeps 25
            } catch { return []; }
          })();
          // Fallback: no audit crawl for this site → pull the connected site's REAL pages & posts
          // from WordPress so the generator has genuine internal-link targets (rather than none, now
          // that it's forbidden from inventing URLs). Skipped silently if not connected / on error.
          if (!internalLinkPages.length) {
            const wpClientForLinks = getClient?.();
            if (wpClientForLinks) {
              try {
                internalLinkPages = (await wpClientForLinks.getAllPagesAndPosts())
                  .filter(p => p.url)
                  .map(p => ({ url: p.url, title: (p.title ?? "").trim() }))
                  .filter(p => { try { return new URL(p.url).pathname !== "/"; } catch { return false; } })
                  .filter((p, i, arr) => arr.findIndex(q => q.url === p.url) === i)
                  .slice(0, 500); // large pool; the server excludes service-area pages, then keeps 25
              } catch { /* keep empty — server adds no links rather than inventing them */ }
            }
          }
          const internalLinks = internalLinkPages.map(p => p.url);
          const res = await fetch("/api/generate-content", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              keyword,
              pageTitle:       getVal(`cft-title-${fixId}`) || getVal(`cft-mt-${fixId}`),
              metaTitle:       getVal(`cft-mt-${fixId}`),
              metaDescription: getVal(`cft-md-${fixId}`),
              pageType:        (clone.getAttribute("data-page-type") as "service" | "article" | null)
                                 || (tags.includes("InnerPage") ? "service" : "article"),
              competitorUrl:   clone.getAttribute("data-competitor-url") || undefined,
              ahrefsKey:       loadAhrefsKey(),
              dfsLogin:        loadDataForSeoCredentials().login,
              dfsPassword:     loadDataForSeoCredentials().password,
              anthropicKey,
              brand,
              location,
              siteUrl,
              internalLinks,
              internalLinkPages,
              internalLinkCount: (() => { const n = parseInt(getVal(`cft-nil-${fixId}`), 10); return Number.isFinite(n) ? Math.max(0, Math.min(5, n)) : undefined; })(),
              customInstructions: getVal(`cft-ci-${fixId}`) || undefined,
              summarize: (document.getElementById(`cft-summarize-${fixId}`) as HTMLInputElement | null)?.checked || undefined,
              summaryHeading: getVal(`cft-summaryheading-${fixId}`) || undefined,
              wordCount: parseInt(getVal(`cft-wc-${fixId}`), 10) || undefined,
              // Site-wide tone/voice (Settings → Global Instructions), applied to all new page content.
              tone: (() => { try { return localStorage.getItem("seo_default_tone") || undefined; } catch { return undefined; } })(),
            }),
            signal: genAbort.signal,
          });
          const result = await res.json() as {
            ok: boolean; content?: string; metaDescription?: string; faqAnswer?: string; error?: string;
            analysis?: { avgKD: number; targetKD: string; topUrls: string[]; competitorKD?: number; targetWords?: number; avgCompetitorWords?: number; wordCountSource?: "manual" | "auto"; internalLinksAdded?: number };
          };

          if (result.ok && result.content) {
            if (contentEl) {
              contentEl.innerHTML = result.content;
              contentEl.querySelectorAll<HTMLAnchorElement>("a[href]").forEach(a => {
                a.title = "Click to edit this link · Ctrl/Cmd+click to open in a new tab";
              });
              // Stash the content-derived FAQ answer for FAQPage schema at publish time.
              if (result.faqAnswer) contentEl.dataset.faqAnswer = result.faqAnswer;
              else delete contentEl.dataset.faqAnswer;
            }
            if (wrapEl)    { wrapEl.style.display = ""; }
            // Reveal the Publish button only now that content exists.
            const submitBtn = document.querySelector<HTMLButtonElement>(`.create-submit-btn[data-fix-id="${fixId}"]`);
            if (submitBtn) submitBtn.style.display = "";
            // Replace the placeholder meta description with the SEO-optimized one.
            if (result.metaDescription) {
              const mdEl = document.getElementById(`cft-md-${fixId}`) as HTMLTextAreaElement | null;
              if (mdEl) mdEl.value = result.metaDescription;
            }
            // Persist the generated content + current field values so switching tabs
            // (which re-renders the section) doesn't wipe it.
            if (contentEl) {
              patchCreateDraft(fixId, {
                content: contentEl.innerHTML,
                faqAnswer: result.faqAnswer,
                fields: captureCreateFields(fixId),
              });
            }
            const a = result.analysis;
            const fmtNum = (n: number) => n.toLocaleString("en-US"); // thousands comma for 4+ digit counts
            let msg = "Content Generated below.";
            if (a) {
              msg += ` SERP average keyword density is ${a.avgKD}% and the target density is between ${a.targetKD}.`;
              msg += a.topUrls.length ? ` ${a.topUrls.length} competitor pages crawled` : " No competitor pages crawled (used AI knowledge)";
              if (a.targetWords) {
                msg += ` and page wordcount target is ${fmtNum(a.targetWords)}`;
                if (a.wordCountSource === "auto" && a.avgCompetitorWords) {
                  msg += ` - (top 5 average is ${fmtNum(a.avgCompetitorWords)} + 15%)`;
                }
              }
              msg += ".";
              const ilc = typeof a.internalLinksAdded === "number" ? a.internalLinksAdded : 0;
              msg += ` ${ilc} internal link${ilc === 1 ? "" : "s"} added.`;
            }
            succeeded = true;
            setStatus(msg, "#16a34a", true);
          } else {
            setStatus(`⚠ ${result.error ?? "Generation failed — check API keys in Settings"}`, "#dc2626");
          }
        } catch (err) {
          // User-cancelled crawl vs. a real network failure.
          if (err instanceof DOMException && err.name === "AbortError") {
            setStatus("Crawl cancelled — no content generated.", "#dc2626");
          } else {
            setStatus(`⚠ Network error: ${err instanceof Error ? err.message : "Request failed"}`, "#dc2626");
          }
        } finally {
          genAbort = null;
          clone.classList.remove("gen-stop");
          clone.disabled = false;
          clone.textContent = succeeded ? "Regenerate Content" : originalLabel;
        }
      });
    });

    // ── Wire brand-mention fetch button ─────────────────────
    // Reads competitors dynamically from localStorage (set when audit runs).
    // Step 1: fetch brand names from homepages. Step 2: run SERP searches.
    el.querySelectorAll<HTMLButtonElement>(".brand-fetch-btn").forEach(btn => {
      const clone = btn.cloneNode(true) as HTMLButtonElement;
      btn.replaceWith(clone);

      clone.addEventListener("click", async () => {
        const { login, password } = loadDataForSeoCredentials();
        if (!login || !password) {
          void showAlert("DataForSEO credentials not configured.\n\nGo to ⚙ Settings → DataForSEO and save your login and password, then try again.", { title: "Credentials Required" });
          return;
        }

        // Resolve domains from cover field + localStorage competitors
        const rawSiteUrl = (document.getElementById("cover-website-url") as HTMLInputElement | null)?.value?.trim()
          || localStorage.getItem("cover_website_url") || "";
        let targetDomain = "";
        try { targetDomain = new URL(rawSiteUrl.startsWith("http") ? rawSiteUrl : `https://${rawSiteUrl}`).hostname.replace(/^www\./, ""); }
        catch { targetDomain = rawSiteUrl.replace(/^www\./, ""); }

        const FALLBACK_COMPETITORS = [
          "maryvillenj.org", "newchapteryouthprogram.com",
          "embarkbh.com", "newbridgehealth.org", "hackensackmeridianhealth.org",
        ];
        let competitorDomains: string[] = [];
        try { competitorDomains = JSON.parse(localStorage.getItem("seo_competitor_domains") ?? "[]"); }
        catch { competitorDomains = []; }
        if (!competitorDomains.length) competitorDomains = FALLBACK_COMPETITORS;

        const allDomains = targetDomain
          ? [targetDomain, ...competitorDomains.filter(d => d !== targetDomain)]
          : competitorDomains;

        if (!allDomains.length) {
          void showAlert("No competitor data found. Run a full audit first so competitors are detected.", { title: "No Competitor Data" });
          return;
        }

        function buildBrandVariants(name: string): string[] {
          if (!name) return [];
          const v = new Set<string>([name]);
          const sd = name.indexOf(" - ");
          if (sd !== -1) { v.add(name.replace(/ - /g, " ")); const a = name.slice(sd + 3).trim(); if (a) v.add(a); }
          else { const d = name.indexOf("-"); if (d !== -1) { v.add(name.replace(/-/g, " ")); const a = name.slice(d + 1).trim(); if (a) v.add(a); } }
          return [...v];
        }

        const statusEl = el.querySelector<HTMLElement>("#brand-mention-status");
        const setStatus = (msg: string, color = "#475569") => {
          if (!statusEl) return;
          statusEl.style.display = "inline"; statusEl.style.color = color; statusEl.textContent = msg;
        };

        const originalText = clone.textContent ?? "🔍 Fetch Brand Mentions";
        clone.disabled = true;
        clone.textContent = "⏳ Fetching…";
        setStatus(`Fetching brand names from ${allDomains.length} homepages…`, "#2563eb");

        try {
          // Step 1: brand names from homepages
          const bnRes = await fetch("/api/fetch-brand-names", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ domains: allDomains }),
          });
          const bnData = await bnRes.json() as { ok: boolean; results?: Array<{ domain: string; brandName: string }>; error?: string };
          if (!bnData.ok || !bnData.results) throw new Error(bnData.error ?? "Brand name fetch failed");
          const brandNameMap = new Map(bnData.results.map(r => [r.domain, r.brandName]));

          const rawBusinessName =
            (document.getElementById("cover-business-name") as HTMLInputElement | null)?.value?.trim()
            || localStorage.getItem("cover_business_name") || brandNameMap.get(targetDomain) || "";

          const sites = allDomains.map((domain, i) => {
            const isTarget = i === 0;
            const brandName = isTarget ? (rawBusinessName || brandNameMap.get(domain) || domain) : (brandNameMap.get(domain) || domain);
            return { domain, brandName, brandVariants: buildBrandVariants(brandName) };
          });

          // Step 2: SERP brand mention searches
          setStatus(`Searching Google across ${sites.length} sites — 30–90 sec…`, "#2563eb");
          const bmRes = await fetch("/api/brand-mentions", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sites: sites.map(({ domain, brandName, brandVariants }) => ({ domain, brandName, brandVariants })), dataForSeoLogin: login, dataForSeoPassword: password }),
          });
          const bmData = await bmRes.json() as { ok: boolean; results?: Array<{ domain: string; urlMentions: number; brandMentions: number }>; error?: string };
          if (!bmData.ok || !bmData.results) throw new Error(bmData.error ?? "Search failed");

          // Rebuild table body
          const tbody = el.querySelector<HTMLElement>("#bm-tbody");
          if (tbody) {
            tbody.innerHTML = bmData.results.map((r, i) => {
              const cid  = r.domain.replace(/[^a-z0-9]/gi, "-");
              const name = brandNameMap.get(r.domain) || r.domain;
              return `<tr${i === 0 ? ' class="yr"' : ""}>
                <td>${i === 0 ? `<strong>${r.domain}</strong>` : r.domain}</td>
                <td><a href="https://${r.domain}" target="_blank" class="visit-link">↗</a></td>
                <td id="bm-url-${cid}">${r.urlMentions.toLocaleString()}</td>
                <td id="bm-brand-${cid}">${r.brandMentions.toLocaleString()} *<br><span style="font-size:10px;color:var(--ink4);font-style:italic;">${name}</span></td>
              </tr>`;
            }).join("");
          }

          setStatus("✓ Data loaded", "#16a34a");
          clone.textContent = "🔄 Refresh";
        } catch (err) {
          setStatus(`⚠ ${err instanceof Error ? err.message : "Request failed"}`, "#dc2626");
          clone.textContent = originalText;
        } finally {
          clone.disabled = false;
        }
      });
    });

    // Google Search Console is now connected per-website in Settings → Saved Websites
    // (GscConnectDialog); the refresh token is stored server-side. Data tabs load it
    // via the site-aware /api/sites/:id/gsc/* routes, so no in-section wiring lives here.

    // ── Hide all action controls in manual-only tabs ────────
    if (id === "citations" || id === "anchors" || id === "competitors") {
      // Hide approve/create buttons but keep Ignore & Done checkboxes
      el.querySelectorAll<HTMLElement>(".ac-btn, .ac-sep, .create-expand-btn, .create-form-row, .create-submit-btn, .create-cancel-btn").forEach(node => { node.style.display = "none"; });
    }

    // ── Gov/Local Links + Ref Domains + Overview + Speed + GBP: hide Approve button + its separator, keep Ignore + Done ──
    if (id === "govlinks" || id === "refdomains" || id === "overview" || id === "speed" || id === "gbp") {
      el.querySelectorAll<HTMLElement>(".ac").forEach(ac => {
        const btn = ac.querySelector<HTMLElement>(".ac-btn");
        if (btn) btn.style.display = "none";
        const firstSep = ac.querySelector<HTMLElement>(".ac-sep");
        if (firstSep) firstSep.style.display = "none";
      });
    }

  }, [html]); // re-wire only when HTML changes (not on every state update)

  // ── Schema tab: an "already on this page" line under each card ────────────────
  // Checklist Step 7 — show the user what structured data is ALREADY live on the page before they
  // click Approve, and attribute its source: schema the tool added earlier via Local SEO Garage
  // (from `_audit_schema`) vs. schema from the theme or another SEO plugin (live-rendered JSON-LD
  // not in `_audit_schema`). The crawl-baked `existingTypes` in the payload gives an instant line
  // with zero network cost; once a WP site is connected we refine it with the source split. Keyed
  // on `connected` too, so connecting after the section renders reveals the attribution.
  useEffect(() => {
    if (id !== "schema") return;
    const el = ref.current;
    if (!el) return;
    let cancelled = false;
    const esc = (s: string) => s.replace(/[&<>]/g, c => (c === "&" ? "&amp;" : c === "<" ? "&lt;" : "&gt;"));

    const lineFor = (card: HTMLElement): HTMLElement => {
      let line = card.querySelector<HTMLElement>(".oc-existing-schema");
      if (!line) {
        line = document.createElement("div");
        line.className = "oc-existing-schema";
        line.style.cssText =
          "margin-top:8px;padding:7px 10px;border-left:3px solid var(--amber-m,#d97706);" +
          "background:var(--amber-l,#fffbeb);border-radius:4px;font-family:Mulish,sans-serif;" +
          "font-size:11px;line-height:1.5;color:var(--ink3,#6b7280);";
        const body = card.querySelector<HTMLElement>(".oc-body") ?? card;
        const action = body.querySelector<HTMLElement>(".oc-action");
        if (action) action.insertAdjacentElement("beforebegin", line);
        else body.appendChild(line);
      }
      return line;
    };

    // Refine one card's line with the source split (LSG vs theme/other). Each card resolves its own
    // page id + reads _audit_schema + fetches live schema, so this is done LAZILY (on scroll-into-
    // view) to avoid firing dozens of WP calls the moment a large Schema tab opens.
    const refine = (card: HTMLElement, url: string): void => {
      const client = getClient?.();
      if (!client || !url) return;
      void describeExistingSchema(url, client).then(sum => {
        if (cancelled || !document.contains(card) || !sum) return;
        const lsg = [...new Set(sum.lsgTypes)];
        const other = [...new Set(sum.otherTypes)];
        if (!lsg.length && !other.length) {
          card.querySelector(".oc-existing-schema")?.remove(); // confirmed nothing live — no false line
          return;
        }
        const parts: string[] = [];
        if (other.length) parts.push(`${esc(other.join(", "))} <em style="font-style:italic">from the theme or another SEO plugin</em>`);
        if (lsg.length) parts.push(`${esc(lsg.join(", "))} <em style="font-style:italic">added earlier via Local SEO Garage</em>`);
        lineFor(card).innerHTML = `<strong>Already on this page:</strong> ${parts.join(" · ")}`;
      });
    };

    const io: IntersectionObserver | null = connected && typeof IntersectionObserver !== "undefined"
      ? new IntersectionObserver((entries, obs) => {
          for (const e of entries) {
            if (!e.isIntersecting) continue;
            const card = e.target as HTMLElement;
            obs.unobserve(card);
            if (card.dataset.exRefined === "1") continue;
            card.dataset.exRefined = "1";
            refine(card, card.dataset.exUrl ?? "");
          }
        }, { rootMargin: "200px" })
      : null;

    el.querySelectorAll<HTMLElement>(".oc").forEach(card => {
      const payload = card.querySelector(".schema-payload")?.textContent?.trim();
      if (!payload) return; // note/SKIPPED cards have no schema to reconcile
      let url = "", baked: string[] = [];
      try {
        const p = JSON.parse(payload) as { url?: unknown; existingTypes?: unknown };
        if (typeof p.url === "string") url = p.url;
        if (Array.isArray(p.existingTypes)) baked = (p.existingTypes as unknown[]).map(String).filter(Boolean);
      } catch { return; }

      // Instant, zero-cost line from the crawl-baked types (source not yet known).
      if (baked.length) {
        lineFor(card).innerHTML = `<strong>Already on this page:</strong> ${esc([...new Set(baked)].join(", "))}`
          + (connected ? "" : ` <span style="opacity:.8">— connect your site to see which was added via Local SEO Garage vs. the theme / another plugin.</span>`);
      }

      // Queue the connected source-split refinement (lazy). Falls back to an immediate call when
      // IntersectionObserver isn't available.
      if (io && url) { card.dataset.exUrl = url; io.observe(card); }
      else if (connected && url && typeof IntersectionObserver === "undefined") refine(card, url);
    });

    return () => { cancelled = true; io?.disconnect(); };
  }, [id, html, connected]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Predict "Manual Update" up front for schema cards ─────────────────────────
  // A schema card is "manual" when approving it would hit the merge popup — the card adds a
  // business/organization entity but the page already has one, so it can't be auto-applied. Instead
  // of the user discovering this only AFTER clicking Approve, check the live page per-card and label
  // the button "Manual Update" before any click. Runs on EVERY tab that has schema cards (Schema,
  // Express Schema, and the schema cards on the Entities tab), lazily on scroll-into-view + only when
  // a WP site is connected. Best-effort: shared page-schema cache keeps this to one read per URL.
  useEffect(() => {
    const el = ref.current;
    if (!el || !connected) return;
    if (!el.querySelector(".schema-payload")) return; // no schema cards in this section
    const client = getClient?.();
    if (!client) return;
    let cancelled = false;

    const predict = (card: HTMLElement): void => {
      const fixId = card.id?.replace(/^oc-/, "");
      const payload = card.querySelector(".schema-payload")?.textContent?.trim();
      if (!fixId || !payload) return;
      if (cbRef.current.manualSchema?.has(fixId)) return; // already known manual — nothing to predict
      let url = "", blocks: Record<string, unknown>[] = [];
      try {
        const p = JSON.parse(payload) as { url?: unknown; blocks?: unknown[] };
        if (typeof p.url === "string") url = p.url;
        if (Array.isArray(p.blocks)) blocks = p.blocks as Record<string, unknown>[];
        else if (Array.isArray(p)) blocks = p as unknown as Record<string, unknown>[];
      } catch { return; }
      if (!url || !blocks.length) return;
      void willSchemaFixBeManual(url, blocks, client).then(isManual => {
        if (!cancelled && isManual) cbRef.current.onPredictManual?.(fixId);
      });
    };

    const io: IntersectionObserver | null = typeof IntersectionObserver !== "undefined"
      ? new IntersectionObserver((entries, obs) => {
          for (const e of entries) {
            if (!e.isIntersecting) continue;
            const card = e.target as HTMLElement;
            obs.unobserve(card);
            if (card.dataset.manualPredicted === "1") continue;
            card.dataset.manualPredicted = "1";
            predict(card);
          }
        }, { rootMargin: "200px" })
      : null;

    el.querySelectorAll<HTMLElement>(".oc").forEach(card => {
      if (!card.querySelector(".schema-payload")) return;
      if (io) io.observe(card);
      else predict(card); // no IntersectionObserver → predict immediately
    });

    return () => { cancelled = true; io?.disconnect(); };
  }, [id, html, connected]); // eslint-disable-line react-hooks/exhaustive-deps

  // Replace {{BRAND}} token in create-form fields with connected site title
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(".cfp-input").forEach(field => {
      const tpl = field.getAttribute("data-brand-tpl") ?? field.value;
      if (!tpl.includes("{{BRAND}}")) return;
      if (!field.getAttribute("data-brand-tpl")) field.setAttribute("data-brand-tpl", tpl);
      field.value = siteBrand ? tpl.replace(/\{\{BRAND\}\}/g, siteBrand) : tpl;
    });
  }, [siteBrand, html]);

  // Sync #overview-kw-target from the live cover-kw-1 input
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const target = el.querySelector<HTMLElement>("#overview-kw-target");
    if (!target) return;

    const sync = () => {
      const kw = (document.getElementById("cover-kw-1") as HTMLInputElement | null)?.value?.trim();
      if (kw) target.textContent = `"${kw}"`;
    };
    sync();

    const kw1Input = document.getElementById("cover-kw-1") as HTMLInputElement | null;
    kw1Input?.addEventListener("input", sync);
    kw1Input?.addEventListener("change", sync);
    return () => {
      kw1Input?.removeEventListener("input", sync);
      kw1Input?.removeEventListener("change", sync);
    };
  }, [html]);

  // Competitors → "Page Inventory" table has 7 columns (Page Category + Yours + 5
  // competitors) and overflows / clips on the right. Tag just that table so CSS can fit
  // all columns (fixed layout + wrapping). Located by its sub-section label text, which
  // CSS alone can't match. Other competitor tables are left untouched.
  useEffect(() => {
    const el = ref.current;
    if (!el || id !== "competitors") return;
    const lbl = Array.from(el.querySelectorAll<HTMLElement>(".sub-lbl"))
      .find(n => /page inventory/i.test(n.textContent ?? ""));
    const table = lbl?.closest(".sub")?.querySelector("table");
    table?.classList.add("pi-fit");
  }, [html, id]);

  // Sync visual state without re-wiring.
  // Iterates ALL .ac elements in the section (not just state entries) so that
  // when a key is deleted from state the DOM classes are correctly reset.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    el.querySelectorAll<HTMLElement>(".ac").forEach(ac => {
      const fixId = ac.id?.replace(/^ac-/, "");
      if (!fixId) return;
      const s = state[fixId]; // undefined when key was deleted

      const btn = ac.querySelector<HTMLButtonElement>(".ac-btn");
      if (btn) {
        // create-expand-btn (Top Pages / Gaps) is a "Create" action even without the
        // data-is-create flag — preserve its class so the form-toggle handler stays wired.
        const isCreate = btn.getAttribute("data-is-create") === "true" || btn.classList.contains("create-expand-btn");
        const baseClass = btn.classList.contains("create-expand-btn") ? "create-expand-btn ac-btn" : "ac-btn";
        // Manual (merge-popup) schema cards apply by hand, so they never queue/complete — the button
        // stays "Manual Update" with no spinner (no `queued`/`done-btn` class).
        if (!isCreate && manualSchema?.has(fixId)) { btn.textContent = "Manual Update"; btn.className = baseClass; }
        else if (s === "done")   { btn.textContent = "✓ Complete"; btn.className = `${baseClass} done-btn`; }
        // Queued (a fix being applied / a page being uploaded): a spinning circle (CSS ::before) is
        // the indicator — no ⏳ text glyph — on both the refined vertical strips and the plain buttons.
        else if (s === "queued") { btn.textContent = "Queued";  btn.className = `${baseClass} queued`; }
        // Refined controls get an icon-free "Approve" (per-card for Internal Links; see acIsRefined).
        else                     { btn.textContent = isCreate ? "Create" : "Approve"; btn.className = baseClass; }
      }

      // Reset icon: shown while queued (clear a hung status) OR done (re-open a
      // completed fix so edited fields can be resubmitted to WordPress). The Internal Links tab has
      // no reset at all — a failed/manual approval reverts the card to Approve on its own.
      const reset = ac.querySelector<HTMLElement>(":scope > .ac-reset");
      if (reset) {
        const showReset = id !== "internal" && (s === "queued" || s === "done");
        reset.style.display = showReset ? "inline-flex" : "none";
      }

      // `ac-vstrip` is re-added on every sync because this line rebuilds className from scratch;
      // it marks the .ac elements that get the Option-3 vertical control strip (see app.css).
      ac.className = "ac" + (acRefined(ac) ? " ac-vstrip" : "") + (s === "done" ? " ac-done" : s === "ignored" ? " ac-ignored" : s === "queued" ? " ac-queued" : "");

      const ss = el.querySelector<HTMLElement>(`#ss-${fixId}`);
      if (ss) {
        ss.className = "ss";
        if (s === "queued")       { ss.className = "ss ss-q show"; ss.innerHTML = "⏳ Approved — queued for WordPress"; }
        else if (s === "done")    { ss.className = "ss ss-d show"; ss.innerHTML = "✓ Complete"; }
        else if (s === "ignored") { ss.className = "ss ss-i show"; ss.innerHTML = "— Ignored"; }
      }

      // Checkbox sync — use querySelectorAll + index so each .ac-chk label's
      // checkbox is addressed independently (first=Ignore, second=Done).
      const cbs = ac.querySelectorAll<HTMLInputElement>(".ac-chk input[type=checkbox]");
      if (cbs[0]) cbs[0].checked = s === "ignored";
      if (cbs[1]) cbs[1].checked = s === "done";

      // Card fade
      ["oc-","um-","gap-","il-","faq-"].forEach(prefix => {
        const card = el.querySelector<HTMLElement>(`#${prefix}${fixId}`);
        if (!card) return;
        card.classList.remove("is-done","is-ignored");
        if (s === "done")    card.classList.add("is-done");
        if (s === "ignored") card.classList.add("is-ignored");
      });
    });

    // "Publish to WordPress" button → "Published" (greyed, disabled) once the page has been
    // successfully published (the fix is marked done). Reverts if the fix is reset.
    el.querySelectorAll<HTMLButtonElement>(".create-submit-btn").forEach(btn => {
      const fixId = btn.getAttribute("data-fix-id");
      if (!fixId) return;
      // origText is the clean label — strip any leading ⚡ that older cached forms baked in.
      if (!btn.dataset.origText) btn.dataset.origText = (btn.textContent || "Publish to WordPress").replace(/^\s*⚡\s*/, "");
      const s = state[fixId];
      if (s === "done") {
        btn.textContent = "Published";
        btn.disabled = true;
        btn.classList.add("published"); btn.classList.remove("publishing");
        // Keep the draft so the published content stays visible in the card after a re-render
        // (tab switch / reload). The draft is only cleared when the card itself is deleted.
      } else if (s === "queued") {
        // Publishing in progress → "Publishing.." + a spinning circle (CSS), disabled to block double-clicks.
        btn.textContent = "Publishing..";
        btn.disabled = true;
        btn.classList.add("publishing"); btn.classList.remove("published");
      } else {
        btn.textContent = btn.dataset.origText;
        btn.disabled = false;
        btn.classList.remove("published", "publishing");
      }
    });

  }, [state, html, manualSchema]); // html → re-sync on new section HTML; manualSchema → relabel "Manual Update"

  // ── Sync hidden state: dim hidden cards (keeping their control strip crisp so
  // they can be un-hidden) and toggle the Hide/Show button. Exports strip them
  // out entirely via stripHiddenCards. ──────────────────────────────────────
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.querySelectorAll<HTMLElement>(".ac").forEach(ac => {
      const fixId = ac.id?.replace(/^ac-/, "");
      if (!fixId) return;
      const isHidden = !!hidden[fixId];
      const card = cardOf(ac);
      card.setAttribute("data-fix-hidden", isHidden ? "true" : "false");

      // Dim the whole card EXCEPT the path down to its control strip, so the card
      // visibly greys out while the Hide/Show + Ignore/Done controls stay crisp and
      // clickable. Walk from the card down to .ac, dimming off-path siblings at each
      // level — this handles nested layouts where content and controls share a
      // wrapper (e.g. .oc > .oc-row > [.oc-body, controls]), which a single pass over
      // the card's direct children would miss entirely.
      let node: HTMLElement | null = card;
      while (node && node !== ac) {
        const kids = Array.from(node.children) as HTMLElement[];
        const onPath: HTMLElement | null = kids.find(k => k === ac || k.contains(ac)) ?? null;
        for (const k of kids) {
          if (k === onPath) continue;            // keep the route to the controls crisp
          k.style.opacity = isHidden ? "0.4" : "";
        }
        node = onPath;
      }

      const hideBtn = ac.querySelector<HTMLButtonElement>(":scope > .ac-hide");
      if (hideBtn) {
        // Plain "Show"/"Hide" text in both strips — no emoji. Refined (vstrip) strips draw the
        // eye-off icon via CSS ::before; horizontal strips stay icon-free (per design).
        hideBtn.textContent = isHidden ? "Show" : "Hide";
        // Styled tooltip (data-tip) instead of the browser's unstyled title="" popup.
        hideBtn.setAttribute("data-tip", isHidden
          ? "Hidden — excluded from exports. Click to show this fix again."
          : "Hide this fix (it doesn't apply). It will be excluded from exports.");
        hideBtn.style.color       = isHidden ? "#dc2626"           : "var(--ink3,#6b7280)";
        hideBtn.style.borderColor = isHidden ? "#fca5a5"           : "var(--bdr,#d1d5db)";
        hideBtn.style.background   = isHidden ? "#fef2f2"           : "#fff";
      }
    });
  }, [hidden, html]);

  // ── Pages & Meta "Tags" field ──────────────────────────────────────────────
  // Inject an editable Tags row under Rec Meta Desc on each card, and populate it
  // with the page's current WordPress tags when connected (shows "none" when the
  // page has no tags). Edits are applied on Approve (see fixExecutor).
  useEffect(() => {
    const el = ref.current;
    if (!el || id !== "urls") return;

    el.querySelectorAll<HTMLElement>(".um").forEach(card => {
      // Internal slug from a sibling editable field (et-{slug}-desc / -title).
      const sib = card.querySelector('[id^="et-"][id$="-desc"]') ?? card.querySelector('[id^="et-"][id$="-title"]');
      const m = sib?.id.match(/^et-(.+)-(?:desc|title)$/);
      const slug = m?.[1];
      if (!slug) return;

      // New-page cards have no "Current …" rows (only existing pages do). On those
      // the Tags field is for the page to be created, and we add a Post/Page selector.
      const isNewPage = !card.querySelector('[id^="cur-"]');

      // Inject the Tags row after the Rec Meta Desc row (once).
      let ta = card.querySelector<HTMLTextAreaElement>(`[id="et-${slug}-tags"]`);
      if (!ta) {
        const descRow = card.querySelector(`[id="et-${slug}-desc"]`)?.closest(".um-row")
          ?? sib?.closest(".um-row");
        if (!descRow) return;
        const ph = isNewPage ? "Add tags to new page (if applicable)" : "No existing tags";
        const hint = isNewPage
          ? "Comma-separated · existing WordPress tags are reused (no duplicates)"
          : "Comma-separated · existing WordPress tags are reused (no duplicates) · applied on Approve";
        const row = document.createElement("div");
        row.className = "um-row";
        row.innerHTML =
          `<div class="um-lbl">Tags</div>` +
          `<div class="um-val">` +
            `<textarea class="um-edit" id="et-${slug}-tags" placeholder="${ph}"></textarea>` +
            `<div class="um-char" style="color:var(--ink4,#94a3b8);font-weight:400">${hint}</div>` +
          `</div>`;
        descRow.insertAdjacentElement("afterend", row);
        ta = row.querySelector("textarea");

        // New pages: add a "Post or Page" selector directly under the Tags field.
        if (isNewPage && !card.querySelector(`[id="et-${slug}-type"]`)) {
          const typeRow = document.createElement("div");
          typeRow.className = "um-row";
          typeRow.innerHTML =
            `<div class="um-lbl">Post or Page</div>` +
            `<div class="um-val">` +
              `<select class="um-edit" id="et-${slug}-type">` +
                `<option value="page" selected>Page</option>` +
                `<option value="post">Post</option>` +
              `</select>` +
              `<div class="um-char" style="color:var(--ink4,#94a3b8);font-weight:400">Choose whether to create this as a WordPress Page or Post</div>` +
            `</div>`;
          row.insertAdjacentElement("afterend", typeRow);
        }
      }
      if (!ta) return;
      const tagsTa = ta;

      // Track manual edits so a (re)load doesn't clobber what the user typed.
      if (!tagsTa.getAttribute("data-wired")) {
        tagsTa.setAttribute("data-wired", "true");
        tagsTa.addEventListener("input", () => tagsTa.setAttribute("data-tags-dirty", "true"));
      }

      // Load current tags from WordPress when connected — existing pages only
      // (a new page has nothing to load).
      if (!isNewPage
          && connected
          && tagsTa.getAttribute("data-tags-loaded") !== "true"
          && tagsTa.getAttribute("data-tags-dirty") !== "true") {
        const href = card.querySelector<HTMLAnchorElement>(".visit-link")?.getAttribute("href")
          ?? card.querySelector(".mono")?.textContent ?? "";
        const pathSlug = slugFromHref(href);
        if (pathSlug) {
          fetchPageTags(pathSlug).then(names => {
            if (names == null) return;                                  // couldn't load — leave editable/empty
            if (tagsTa.getAttribute("data-tags-dirty") === "true") return; // user typed meanwhile
            const joined = names.join(", ");
            tagsTa.value = joined;
            tagsTa.setAttribute("data-tags-loaded", "true");
            tagsTa.setAttribute("data-tags-baseline", joined); // for change detection on Approve
          }).catch(() => { /* non-fatal */ });
        }
      }
    });
  }, [id, html, connected, fetchPageTags]);


  return (
    <>
      <div
        ref={ref}
        dangerouslySetInnerHTML={{ __html: processedHtml }}
      />
      {parentPicker && (
        <ParentPickerDialog
          title={parentPicker.mode === "post" ? "Search Post Category" : "Search Parent"}
          label={parentPicker.mode === "post" ? "Post Category" : "Parent Page"}
          load={async () => {
            const client = getClient?.();
            if (!client) return [];
            if (parentPicker.mode === "post") {
              const cats = await client.getCategories();
              return cats
                .map(c => ({ value: c.name, label: c.name }))
                .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
            }
            const pages = (await client.getAllPagesAndPosts()).filter(p => p.type === "page");
            return pages
              .map(p => {
                // Show the URL path without the domain (same as the Bulk Edit parent dropdown).
                let path = p.slug ? `/${p.slug}` : "";
                try { path = new URL(p.url).pathname.replace(/\/$/, "") || "/"; } catch { /* keep slug fallback */ }
                return { value: path, label: path };
              })
              .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }));
          }}
          onClose={() => setParentPicker(null)}
          onSelect={value => {
            const input = document.getElementById(`cft-par-${parentPicker.fixId}`) as HTMLInputElement | null;
            if (input) { input.value = value; input.dispatchEvent(new Event("input", { bubbles: true })); }
            setParentPicker(null);
          }}
        />
      )}
    </>
  );
}
